

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HEADER_TITLES, PROFILE_TAB_TITLES, pageTitle, VIEW_TITLES } from './utils/pageTitle';
import { SideNav } from './components/SideNav';
import { ChatInterface } from './components/ChatInterface';
import { RightSidebar } from './components/RightSidebar';
import { trackClick } from './utils/analytics';
import { ModalType, Chat, ChatMessage, Sender, ProfileData, InterestsData, InboxConversation, MessageStatus, InboxMessageItem, Attachment, SavedCardType } from './types';

// Legacy RDProfileData kept locally for backward compat
interface RDProfileData {
  collaborationTypes: string[];
  collaborationLead: string[];
  collaborationTimeline: string;
  rdBudget: string;
  otherInfo: string;
  corporateDeckUrl: string;
  rdSummary: string;
  corporateDeckFile: string;
  companyLinkedinPage?: string;
}
import { InviteModal } from './components/modals/InviteModal';
import { FeedbackModal } from './components/modals/FeedbackModal';
import { BugReportModal } from './components/modals/BugReportModal';
import { generateResponse, generateChatTitle, generateSamplePrompts } from './services/geminiService';
import { apiService, BILLING_SUMMARY_EVENT, BillingState, BillingTrialLifecycle, TrialForm } from './services/apiService';
import { friendlyAuthError } from './utils/authErrors';
import { keywordsCache } from './utils/keywordsCache';
import { BookCallModal } from './components/modals/BookCallModal';
import { ProfilePage } from './components/ProfilePage';
import { ChangePasswordModal } from './components/modals/ChangePasswordModal';
import { DeleteAccountModal } from './components/modals/DeleteAccountModal';
import { ForYouPage } from './components/ForYouPage';
// The pre-24-Oct For You page: what members see until "foryou" is enabled for them.
import { ForYouPageLegacy } from './components/ForYouPageLegacy';
import { useFeatureAccess, type FeatureKey } from './utils/featureAccess';
import { AdminPreviewBadge, AdminPreviewBar } from './components/AdminPreview';
import { WhatsNewPage } from './components/whatsnew/WhatsNewPage';
import { setCosmosSeed } from './utils/cosmosSeed';
import { clearReturnAfterAuth, getResolvedReturn, setResolvedReturn } from './utils/returnAfterAuth';
import { goBackAfterAuth, stripNextFromAddress } from './utils/returnAfterAuthNav';
import { DirectSearchPage } from './components/DirectSearchPage';
import { canSeeDirectSearch, shouldUseAlgoliaForCosmos } from './utils/featureFlags';
import TechCard from './components/TechCard';
// Accordion tech card (Admin → Tech URL pages → "Tech card design"); TechCard above stays the legacy path.
import TechAccordionCard from './components/techcard/TechAccordionCard';
import { useTechCardDesign } from './components/techcard/track';
import { EmptyInbox } from './components/EmptyInbox';
import { ContactUniversityModal } from './components/modals/ContactUniversityModal';
import { PreviewMessageModal } from './components/modals/PreviewMessageModal';
import { SentMessageModal } from './components/modals/SentMessageModal';
import { InboxPage } from './components/InboxPage';
import { FALLBACK_SAMPLE_PROMPTS, MIN_INTERESTS_FOR_RECOMMENDATIONS } from './constants';
import ResearcherCard from './components/ResearcherCard';
import { HamburgerMenuIcon, ClipboardTextIcon, MenuCloseIcon } from './components/icons';
import { LoadingScreen, ChatLoadingSkeleton, InboxLoadingSkeleton } from './components/LoadingScreen';
import { ProfileCompletionPopup } from './components/ProfileCompletionPopup';
import { startPlanCheckout } from './utils/razorpayCheckout';
import { ContactTTOModal } from './components/modals/ContactTTOModal';
import { ConciergeRequestModal } from './components/modals/ConciergeRequestModal';

/**
 * We show the TTO's own address rather than sending an email on the user's
 * behalf — we are not making introductions. Flip to false to restore the old
 * compose-and-send flow, which is still wired up below.
 */
const USE_TTO_CONTACT_MODAL = true;
import OceanPage from './components/OceanPage';
import ChallengesPage from './components/ChallengesPage';
// OceanApp also loads ./components/ocean/flags, which stashes an OCEAN share
// link (?ocean=problem:<id>) at page load.
import OceanApp from './components/ocean/OceanApp';
import IpIntelReportPage from './landing-page/components/IpIntelReportPage'; // FE-B: in-platform IP Intel tab
import OnboardingModal from './components/OnboardingModal';
// Onboarding v1 (flow 'v1' — Roll back in Admin → Onboarding): today's modal, unchanged.
import OnboardingModalLegacy from './components/OnboardingModalLegacy';
import {
  fallbackOnboardingConfig, OnbEntry, OnbFlow, OnboardingPublicConfig, OnboardingResult,
  whenLandingDecided,
} from './types/onboarding';
import { trackOnboardingConfigError } from './utils/onboardingTrack';
import PricingModal from './components/PricingModal';
import TrialFormModal from './components/TrialFormModal';
import { BILLING_CHANGED_EVENT, useUpgradeGate } from './components/useUpgradeGate';
import { UpgradeNavButton } from './components/upgrade/UpgradePrompts';
import {
  fillUpgradeText, markSigninModalShown, promptFeature, setUpgradeViewer, signinModalDue,
  trackUpgradePrompt, useUpgradePrompts,
} from './components/upgrade/promptConfig';
import { setCopyViewer } from './utils/copyGuard';
import { PrivacyPolicyPage } from './components/PrivacyPolicyPage';
import { TermsOfServicePage } from './components/TermsOfServicePage';
import AboutUsPage from './components/AboutUsPage';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';


const getInitialProfileData = (email: string): ProfileData => ({
  email: email || 'user@example.com',
  firstName: '',
  lastName: '',
  contactNumber: '',
  countryCode: '91',
  country: '',
  city: '',
  companyName: '',
  designation: '',
  companyWebsite: '',
  qualification: '',
  linkedin: '',
  summary: '',
  hats: [],
  intent: [],
  profileImage: undefined
});

const PROFILE_WEIGHTS: Record<string, number> = {
  firstName: 10, lastName: 10, email: 10,
  contactNumber: 15, city: 5, country: 5,
  companyName: 5, designation: 5, companyWebsite: 5,
  qualification: 5, linkedin: 15,
  hats: 15, intent: 15,
};
const PROFILE_MAX = Object.values(PROFILE_WEIGHTS).reduce((a, b) => a + b, 0);

/**
 * Defensive cleanup for the `researchers` array returned by the technology
 * detail endpoint. Some rows in the upstream `researchers` table have patent
 * reference strings (e.g. "IITM IDF Ref. 1758 Patent Application No:…") sitting
 * in the `researcher_name` column instead of an actual person's name — when we
 * render them as researchers it looks broken. Until the DB is cleaned we
 * filter those out client-side.
 *
 * Heuristic: drop names that match common patent/IP boilerplate tokens. Keep
 * everything else (real names will pass through untouched).
 */
const cleanResearchers = (raw: any[] | undefined): { name: string; url: string }[] => {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const PATENT_NOISE = /\b(IDF Ref|Patent Application|Patent No\.?|Application No\.?|priority date|invention disclosure)\b/i;
  return raw
    .filter(r => r && typeof r.name === 'string' && r.name.trim() && !PATENT_NOISE.test(r.name))
    .map(r => ({ name: r.name.trim(), url: r.url || '' }));
};

const initialRDProfileData: RDProfileData = {
  collaborationTypes: [],
  collaborationLead: [],
  collaborationTimeline: 'Select one',
  rdBudget: 'Select one',
  otherInfo: '',
  corporateDeckUrl: '',
  rdSummary: '',
  corporateDeckFile: '',
};

const initialInterestsData: InterestsData = {
  keywords: [
    "Agriculture & Horticulture",
    "Food & Beverage",
    "Cleantech & Environmental",
    "Water Resource Efficiency",
    "Crop Improvement",
    "Produce Packaging & Storage",
  ],
  emailAlerts: false,
};

// ──────────────────────────────────────────────────────────────────────────
// DO NOT use this as the source of truth for any user-visible value, ESPECIALLY
// not for outbound email. Live tech data must come from `selectedCardData`
// (which is loaded via `apiService.getTechnologyDetails(...)`).
//
// History: this object was once read directly inside the email-build code for
// title / researcher salutation / id and combined with a stale
// `selectedCardData` that never carried `tech_manager_*`, which caused a real
// lead on 2026-05-13 to be sent with the wrong title, wrong recipient name
// and a hardcoded dev fallback TO address. See
// Backend/DOCS/BUG_2026-05-13_misrouted_tto_email.md.
//
// It is left in place ONLY as a never-fired safety net at the very tail of a
// `selectedCardData?.X || techCardData.X` chain so the component never crashes
// if it renders before a card is selected. Any new code referencing this
// directly (without the `selectedCardData?.X || …` guard) is a regression.
// ──────────────────────────────────────────────────────────────────────────
const techCardData = {
    id: "UA25-211",
    relevanceScore: 92,
    title: "Martin Irrigation Head: Efficient Solid Set Flood Irrigation, Minimizing Water & Labor",
    institution: "University of Arizona",
    location: "United States",
    trl: "TRL 5",
    researchers: [
        { name: "Robert A. Martin" },
        { name: "Maria A. Rodriguez" }
    ],
    summary: "This technology provides an efficient and cost-effective method for solid set flood irrigation, significantly reducing water consumption and labor requirements compared to traditional methods. It is designed for easy installation and maintenance, making it suitable for a wide range of agricultural applications. The system has been validated in relevant environments and is ready for large-scale implementation."
};

const researcherCardData = {
    relevanceScore: 55,
    title: "Robert Masson",
    institution: "University of Arizona",
    location: "United States",
    techs: [
        { id: "UA25-211", title: "Martin Irrigation Head: Efficient Solid Set Flood Irrigation, Minimizing Water & Labor" },
        { id: "UA25-212", title: "Targeted Oral Pro-Drug Delivery for GI Therapies" },
        { id: "UA25-213", title: "Iterative Nanopore Sequencing for RNA Quality Control" },
        { id: "UA25-214", title: "Precision Therapy via DNA Modeling for Personalized Medicine" },
        { id: "UA25-215", title: "Efficient Synthesis of Deuterated Amino Acids" },
        { id: "UA25-216", title: "Another great technology by Robert Masson" }
    ],
};

// ──────────────────────────────────────────────────────────────────────────
// `/?view=billing` — the deep link behind the trial e-mails' "Upgrade" button.
// The MEMAC e-mails' buttons (Backend/memac.py GO_TARGETS, via
// /api/v1/go/<target>) add `profile`, `interests`, `directsearch` and
// `foryou`; DEEPLINK_LANDING says where each lands.
//
// The person clicking it is usually signed out, so the query string does not
// survive to the moment this component mounts: OAuth goes out to the provider
// and comes back on "/", and the landing page / profile gate sit in between.
// So the intent is written down at page load (this module is imported
// statically by index.tsx, so this runs before anyone signs in) and read back
// when the signed-in app mounts. Tab-scoped (sessionStorage) and short-lived,
// so an old click cannot hijack a later, unrelated sign-in. Every other
// `view=` value is left to the existing
// handling below, i.e. the usual home.
// ──────────────────────────────────────────────────────────────────────────
const DEEPLINK_VIEW_KEY = 'utech_deeplink_view';
const DEEPLINK_TTL_MS = 30 * 60 * 1000;
/** view → the app view to open, Profile's tab, or the interests picker
 *  (onboarding) over the home — Profile's Interests tab is hidden. */
const DEEPLINK_LANDING: Record<string, { view?: string; tab?: string; onboarding?: boolean }> = {
  billing: { view: 'profile', tab: 'billing' },
  profile: { view: 'profile', tab: 'profile' },
  interests: { onboarding: true },
  directsearch: { view: 'directsearch' },
  foryou: { view: 'foryou' },
  // OCEAN share links: /?view=ocean&ocean=problem:<id> (components/ocean/flags.ts).
  ocean: { view: 'ocean' },
  // What's New (MEMAC's "See what's new" button sends ?view=whats-new).
  whatsnew: { view: 'whatsnew' },
  'whats-new': { view: 'whatsnew' },
};
/** Deep-link views behind feature access (utils/featureAccess.ts): followed
 *  only once the server says the account may open them; otherwise the link
 *  lands on the usual home, as before those pages existed. (`foryou` is not
 *  here: that view always exists — feature access picks which page it shows.) */
const GATED_VIEW_FEATURE: Record<string, FeatureKey> = { whatsnew: 'whatsnew', ocean: 'ocean' };
/** The main area of a gated view while feature access is still unknown, so
 *  neither the gated page nor its fallback mounts (and calls out) first. */
const FeatureAccessWait: React.FC = () => (
  <main className="flex flex-1 self-stretch items-center justify-center rounded-2xl bg-white shadow-custom-light">
    <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" role="status" aria-label="Loading" />
  </main>
);
const isDeepLinkView = (v: any): v is string =>
  typeof v === 'string' && Object.prototype.hasOwnProperty.call(DEEPLINK_LANDING, v);
try {
  const v = new URLSearchParams(window.location.search).get('view');
  if (window.location.pathname === '/' && isDeepLinkView(v)) {
    sessionStorage.setItem(DEEPLINK_VIEW_KEY, JSON.stringify({ view: v, at: Date.now() }));
  }
} catch { /* storage blocked — the in-URL check below still covers a signed-in click */ }

/** The deep link this mount should follow (a DEEPLINK_LANDING key), or null.
 *  Pure (no removal): React may call a state initializer twice, so the stash
 *  is cleared in an effect after mount instead. */
const deepLinkView = (): string | null => {
  try {
    const inUrl = new URLSearchParams(window.location.search).get('view');
    if (isDeepLinkView(inUrl)) return inUrl;
    const raw = sessionStorage.getItem(DEEPLINK_VIEW_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return isDeepLinkView(v?.view) && Date.now() - Number(v?.at || 0) < DEEPLINK_TTL_MS ? v.view : null;
  } catch {
    return null;
  }
};

interface AppProps {
  loggedInEmail: string | null;
  onLogout?: () => void;
}

const App: React.FC<AppProps> = ({ loggedInEmail, onLogout }) => {
  // Read once, before the view state below is initialised from it.
  const [deepLink] = useState(deepLinkView);
  const landing = deepLink ? DEEPLINK_LANDING[deepLink] : null;
  // Which gated pages this account may open (new For You, What's New, OCEAN):
  // asked once after sign-in; nothing gated counts as enabled until it answers.
  const access = useFeatureAccess();
  /** A deep link to a gated view, held until feature access answers. */
  const [pendingGatedView, setPendingGatedView] = useState<string | null>(
    () => (landing?.view && GATED_VIEW_FEATURE[landing.view] ? landing.view : null));
  /** That held link was followed (feature access let this account in). */
  const [gatedLinkFollowed, setGatedLinkFollowed] = useState(false);
  /** The deep link really brought them to a page (for "nothing over a
   *  deep-linked page", e.g. the sign-in plans prompt). A gated link counts
   *  while it is held — it may yet be followed — and once followed; a dropped
   *  one is no deep link at all, exactly as before those pages existed. */
  const deepLinkLanded = !!deepLink
    && (!(landing?.view && GATED_VIEW_FEATURE[landing.view]) || pendingGatedView !== null || gatedLinkFollowed);
  /** A NEW account that signed up from a public tech / university page
   *  (utils/returnAfterAuth): onboarding first, then back to that page once
   *  it is finished or skipped — or at once when there is no onboarding to
   *  show. (An existing account never gets here: MainWithClerk sends it
   *  straight back.) MainWithClerk decided it for THIS sign-in (armed page
   *  only); read once, at mount. ?next= never stays in the address. */
  const [authReturn] = useState(() => {
    const e = getResolvedReturn();
    setResolvedReturn(null);
    return e && e.newAccount ? e : null;
  });
  const authReturnRef = useRef(authReturn);
  useEffect(() => { stripNextFromAddress(); }, []);
  /** Not going back after all (a trial chosen in onboarding): forget it. */
  const abandonAuthReturn = () => {
    if (!authReturnRef.current) return;
    authReturnRef.current = null;
    clearReturnAfterAuth();
    stripNextFromAddress();
  };
  /** Back to the page; false when that is not possible (the app carries on). */
  const returnAfterOnboarding = (): boolean => {
    const e = authReturnRef.current;
    if (!e) return false;
    authReturnRef.current = null;
    const ok = goBackAfterAuth(e, { newAccount: true });
    if (!ok) stripNextFromAddress();
    return ok;
  };
  useEffect(() => {
    try { sessionStorage.removeItem(DEEPLINK_VIEW_KEY); } catch { /* nothing to clear */ }
  }, []);
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({});
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const hasInitializedChat = useRef(false); // Track if we've already triggered add chat on initial load
  useEffect(() => {
    const userId = apiService.getStoredUserId();
    if (!userId) { setOnboardingConfigResolved(true); return; }
    // Which onboarding flow, its chips and copy. An older server has no such
    // route (404): that is flow v1, today's behaviour.
    const flowP: Promise<OnbFlow | null> = apiService.getOnboardingConfig()
      .catch(() => null)
      .then(cfg => {
        const cfgData = cfg && cfg.success ? cfg.data : undefined;
        if (cfgData && cfgData.success !== false && (cfgData.flow === 'v1' || cfgData.flow === 'v2')) {
          setOnboardingConfig(cfgData);
          return cfgData.flow;
        }
        trackOnboardingConfigError(cfg?.status ?? null, cfg?.error ?? null);
        return null;
      });
    // Whether the modal opens (either flow) — set the moment it is known, so
    // under v2 the modal is already up when Cosmos learns the flow.
    const statusP = apiService.getOnboardingStatus(userId)
      .catch(() => null)
      .then(st => {
        setOnboardingEntry(landing?.onboarding ? 'deeplink_interests'
          : st?.success && st.data?.entry_hint === 'resume' ? 'resume' : 'first_run');
        // ?view=interests (MEMAC's "Add your interests") opens the same picker.
        const needed = !!(landing?.onboarding || (st?.success && st.data?.success && st.data.needed));
        if (needed) setShowOnboarding(true);
        // Signed up from a tech page but nothing to onboard (or the status
        // could not be read): straight back to the page.
        else if (authReturnRef.current) returnAfterOnboarding();
      });
    // Cosmos holds an Explorer's first list until it knows which one (no
    // Popular flash before the picks): flow v1, or no answer, resolves the
    // moment /onboarding/config does, so v1 Popular loads at once as before;
    // only v2 also waits for the status call. The billing summary and the
    // profile spec never gate it. LANDING_SAFETY_MS is the v2 safety net.
    const cancelLanding = whenLandingDecided(flowP, statusP, () => setOnboardingConfigResolved(true));
    (async () => {
      // The plans/trial state still lands no earlier than the status, as it
      // always did: the sign-in plans prompt (gated on upgradePlans) must see
      // showOnboarding before it can fire.
      const [, spec, summary] = await Promise.all([
        statusP,
        apiService.getProfileSpec(),
        apiService.getBillingSummary(userId),
      ]);
      if (spec.success && spec.data?.success) {
        const list = (spec.data.options as any)?.industries;
        setOnboardingIndustries(Array.isArray(list) ? list : []);
      }
      if (summary.success && summary.data?.success) {
        billingUidRef.current = userId;
        setUpgradePlans(summary.data.plans || []);
        setUpgradeFeatureOrder(summary.data.feature_order || []);
        // The paywall marks the user's current plan; it must come from the
        // subscription, not a constant.
        const sub = (summary.data as any).subscription;
        setCurrentPlanCode(
          sub && (sub.status === 'active' || sub.status === 'cancelled')
            ? sub.plan_code : 'explorer');
        setUpgradeTrial((summary.data as any).trial || null);
        setUpgradeState((summary.data as any).state || null);
        setUpgradeTrialLifecycle((summary.data as any).trial_lifecycle || null);
        // Same test BillingTab uses, so the two surfaces cannot disagree.
        setOnTrial(!!(sub && (sub.is_trial || String(sub.source || '').startsWith('trial'))));
      }
    })();
    return cancelLanding;
  }, []);

  const [activeView, setActiveView] = useState(() => {
    // Check URL parameters for legal page views
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    if (view === 'privacy-policy' || view === 'terms-of-service') {
      return view;
    }
    // A deep link (see deepLinkView): ?view=billing → Profile, opened on
    // Billing; profile → Profile; foryou → For You; directsearch → Search.
    // whatsnew / ocean wait for feature access (pendingGatedView).
    if (landing?.view && !GATED_VIEW_FEATURE[landing.view]
        && (landing.view !== 'directsearch' || canSeeDirectSearch(loggedInEmail))) {
      return landing.view;
    }
    // Direct Search is now the primary product surface for users who have
    // access; everyone else lands on Cosmos as before. The Cosmos nav item
    // is also hidden in SideNav for Direct Search users.
    if (canSeeDirectSearch(loggedInEmail)) {
      return 'directsearch';
    }
    return 'cosmos';
  });
  const [profileData, setProfileData] = useState<ProfileData>(getInitialProfileData(loggedInEmail || ''));
  const [rdProfileData, setRDProfileData] = useState<RDProfileData>(initialRDProfileData);
  const [interestsData, setInterestsData] = useState<InterestsData>(initialInterestsData);
  const [techCardModalOpen, setTechCardModalOpen] = useState(false);
  const [researcherCardModalOpen, setResearcherCardModalOpen] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [selectedCardData, setSelectedCardData] = useState<any>(null);
  const [cardDataLoading, setCardDataLoading] = useState(false);
  // Accordion card: the design switch, the result's real relevance (never the
  // 85 fallback), and a tech whose card payload failed (legacy card for it).
  const techCardDesign = useTechCardDesign();
  const [cardRelevance, setCardRelevance] = useState<number | null>(null);
  const [accordionFailedId, setAccordionFailedId] = useState<string | null>(null);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSummaryType, setContactSummaryType] = useState<'personal' | 'rd'>('personal');
  // Resolved by ContactUniversityModal from tech_manager_details /
  // tech_manager_default, so the preview greets the person the email is
  // actually going to rather than the first listed researcher.
  const [contactTechManagerName, setContactTechManagerName] = useState('');
  const [inboxConversations, setInboxConversations] = useState<InboxConversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  // Removed isReplying state - no longer needed since automatic replies are disabled
  const [samplePrompts, setSamplePrompts] = useState(FALLBACK_SAMPLE_PROMPTS);
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(false);
  const [profileInitialTab, setProfileInitialTab] = useState(landing?.tab || 'profile');
  // The Profile tab actually open (ProfilePage reports it): the mobile header
  // and the browser tab say "Billing" / "Saved" rather than just "Profile".
  const [profileTab, setProfileTab] = useState<string>(landing?.tab || 'profile');
  // The tab title names the view: "UniversityTech.io | Search", "… | Billing".
  useEffect(() => {
    const name = activeView === 'profile'
      ? (PROFILE_TAB_TITLES[profileTab] || VIEW_TITLES.profile) : VIEW_TITLES[activeView];
    document.title = pageTitle(name);
  }, [activeView, profileTab]);
  const headerTitle = activeView === 'profile'
    ? (PROFILE_TAB_TITLES[profileTab] || HEADER_TITLES.profile)
    : (HEADER_TITLES[activeView] || 'Cosmos');
  /** Profile -> Billing. Both checkouts live there, so anything that needs one
   *  — a paywall, an autopay trial — hands over instead of duplicating it. */
  const goToBilling = () => { setProfileInitialTab('billing'); setActiveView('profile'); };

  /**
   * "Start free trial", in place: open the details sheet (Sep 2026 spec).
   * The trial asks for no card; it asks who the person is — name,
   * organization, designation — and the server will not grant it without
   * them, so the sheet stands between the button and the API on every route.
   * Used by the paywall modal and by onboarding, so neither hands the person
   * off to the Billing tab to press a second button — which is what the
   * button they just pressed already promised to do. The sheet is prefilled
   * from the profile already in hand; `submitTrialInPlace` does the rest.
   */
  const startTrialInPlace = async (plan?: any, origin: string = 'unknown') => {
    // Where the trial was asked for — the paywall ('paywall:<feature>'), the
    // Cosmos picks' block, legacy onboarding — kept on the trial row for
    // attribution, so it must pass the server's rule: lower case, up to 60
    // characters of letters, digits and _ : . - or space.
    const from = (origin || '').toLowerCase().replace(/[^a-z0-9_:. -]+/g, '').replace(/\s+/g, ' ')
      .trim().slice(0, 60) || 'unknown';
    const uid = apiService.getStoredUserId() || billingUidRef.current;
    if (!uid) { alert('Please sign in again to continue.'); return; }
    // The trial payload arrives with the billing summary fetched on mount.
    // Onboarding can finish before that lands, and a null payload would
    // fall through to the card-free route for someone whose admin has the
    // legacy card-first trial switched on — so ask again here rather than
    // guess.
    let trialInfo = upgradeTrial;
    let plans = upgradePlans;
    if (!trialInfo) {
      const sum = await apiService.getBillingSummary(uid);
      if (sum.success && sum.data?.success) {
        trialInfo = sum.data.trial || null;
        plans = sum.data.plans || [];
        setUpgradeTrial(trialInfo); setUpgradePlans(plans);
        setUpgradeState(sum.data.state || null);
        setUpgradeTrialLifecycle(sum.data.trial_lifecycle || null);
      }
    }
    if (!trialInfo?.available) {
      alert(trialInfo?.reason || 'A trial is not available for this account right now.');
      return;
    }
    setTrialForm({
      plan, uid, trialInfo, plans, origin: from,
      initial: {
        first_name: profileData.firstName, last_name: profileData.lastName,
        organization: profileData.companyName, designation: profileData.designation,
      },
    });
    trackClick('trial_form_open', { origin: from });
  };

  /**
   * The sheet's Start trial: the four details go with the request, the
   * server writes them to the profile and grants access at once, and the
   * locks lift here and now. No card and no Razorpay sheet — the autopay
   * trial was retired on 2026-09-23.
   */
  const submitTrialInPlace = async (form: TrialForm): Promise<{ ok: boolean; error?: string }> => {
    const ctx = trialForm;
    if (!ctx) return { ok: false, error: 'Please try again.' };
    const { uid, trialInfo, origin } = ctx;
    const done = async () => {
      setTrialForm(null);
      setIntroPricing(false); upgrade.closeUpgrade();
      upgrade.refresh(); refreshProfileData?.();
      // The trial is now used, and the plan is now current. Without this a
      // later paywall in the same session still offered "Start free trial"
      // — a door the server would refuse to open.
      const sum = await apiService.getBillingSummary(uid);
      if (sum.success && sum.data?.success) {
        const sub = (sum.data as any).subscription;
        setUpgradeTrial((sum.data as any).trial || null);
        setUpgradeState((sum.data as any).state || null);
        setUpgradeTrialLifecycle((sum.data as any).trial_lifecycle || null);
        setOnTrial(!!(sub && (sub.is_trial || String(sub.source || '').startsWith('trial'))));
        setCurrentPlanCode(sub && (sub.status === 'active' || sub.status === 'cancelled')
          ? sub.plan_code : 'explorer');
      }
    };
    trackClick('trial_form_submit', { origin });
    const r = await apiService.billingStartTrial(uid, form, origin);
    if (!(r.success && r.data?.success)) {
      const error = r.error || r.data?.detail || 'Could not start the trial.';
      trackClick('trial_form_error', { origin, status: r.status ?? null, error: String(error).slice(0, 120) });
      return { ok: false, error };
    }
    trackClick('trial_started', { origin, days: r.data.days ?? trialInfo?.days ?? null });
    void done();
    showTrialNotice(r.data.days ?? trialInfo.days);
    return { ok: true };
  };
  /** The success line, as a toast: there is no page behind the paywall to
   *  print it on, and an alert() over the results they just unlocked would
   *  undo the point of unlocking them. */
  const showTrialNotice = (days?: number) => {
    setTrialNotice(days ? `Your ${days}-day trial is active — enjoy.` : 'Your trial is active — enjoy.');
    window.setTimeout(() => setTrialNotice(''), 8000);
  };
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [isChatsLoaded, setIsChatsLoaded] = useState(false);
  const [isInboxLoading, setIsInboxLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [inboxSavedStates, setInboxSavedStates] = useState<Map<string, {bookmarked: boolean, liked: boolean | null}>>(new Map());
  const [showProfileCompletionPopup, setShowProfileCompletionPopup] = useState(false);
  // First-run onboarding: two questions, then real results. Shown once, and
  // never on top of the profile-completion nudge.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingIndustries, setOnboardingIndustries] = useState<string[]>([]);
  // Onboarding v2 (spec 13 Oct 2026). The public config picks the flow: v1 is
  // the legacy modal, the plans sheet after it and Popular — exactly as
  // before; v2 is the two skippable steps and, for Explorers, their 3 picks
  // in Cosmos with no sheet on entry. Null until it answers, and for good on
  // an older server, which means v1. `resolved` once the mount lookups are
  // in (or gave up), so Cosmos knows whether to wait for the picks.
  const [onboardingConfig, setOnboardingConfig] = useState<OnboardingPublicConfig | null>(null);
  const [onboardingConfigResolved, setOnboardingConfigResolved] = useState(false);
  const [onboardingEntry, setOnboardingEntry] = useState<OnbEntry>('first_run');
  /** The v2 modal's answer, for Cosmos's first landing (no storage: a later
   *  visit lets the server read the saved choice). */
  const [explorerChoice, setExplorerChoice] = useState<(OnboardingResult & { at: number }) | null>(null);
  const onboardingFlow: 'v1' | 'v2' = onboardingConfig?.flow === 'v2' ? 'v2' : 'v1';
  /** The config the v2 modal gets. With no industries in it (a server-side
   *  hiccup), the profile spec's list instead, chips fetched per industry
   *  (an empty config_version tells the modal so). */
  const onboardingModalConfig: OnboardingPublicConfig | null = !onboardingConfig ? null
    : onboardingConfig.industries?.length ? onboardingConfig
    : onboardingIndustries.length ? {
        ...fallbackOnboardingConfig(onboardingIndustries, onboardingConfig.flow),
        explorer_picks: onboardingConfig.explorer_picks,
        entry_modal: onboardingConfig.entry_modal,
        ...(onboardingConfig.copy ? { copy: onboardingConfig.copy } : {}),
      }
    : null;
  /** Whether a modal is actually on screen (each flow has its own guard). */
  const onboardingVisible = showOnboarding
    && (onboardingFlow === 'v2' ? !!onboardingModalConfig : onboardingIndustries.length > 0);
  // Signed up from a tech page and onboarding is due, but its modal cannot
  // open (no config / no industries): do not strand them — back to the page.
  useEffect(() => {
    if (!authReturnRef.current || !showOnboarding || onboardingVisible) return;
    const t = window.setTimeout(() => { returnAfterOnboarding(); }, 15000);
    return () => window.clearTimeout(t);
    // returnAfterOnboarding reads a ref only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnboarding, onboardingVisible]);
  // Entitlements + the shared upgrade modal for every locked action.
  const upgrade = useUpgradeGate();
  const [upgradePlans, setUpgradePlans] = useState<any[]>([]);
  const [upgradeFeatureOrder, setUpgradeFeatureOrder] = useState<string[]>([]);
  const [upgradeBusyPlan, setUpgradeBusyPlan] = useState('');
  /** The plan the user is actually on, for the paywall's "(current)" marker. */
  const [currentPlanCode, setCurrentPlanCode] = useState<string>('explorer');
  /** Whether this account can start a trial, and by which route. From the
   *  same billing summary as the plans, so the paywall never offers a trial
   *  the server would refuse. */
  const [upgradeTrial, setUpgradeTrial] = useState<any>(null);
  /** Where the account is in the trial lifecycle (explorer | trial_active |
   *  trial_lapsed | voyager) and the running trial's dates, from the same
   *  summary. The paywall's trial button has three faces — offered, greyed
   *  with the reason, "Trial active · n days left" — and `trial.available`
   *  alone cannot tell the last two apart. */
  const [upgradeState, setUpgradeState] = useState<BillingState | null>(null);
  const [upgradeTrialLifecycle, setUpgradeTrialLifecycle] = useState<BillingTrialLifecycle | null>(null);
  /** The trial details sheet: null when closed, else everything its submit
   *  needs, captured at open so a summary refresh mid-typing cannot change
   *  the route under it. */
  const [trialForm, setTrialForm] = useState<{
    plan?: any; uid: string; trialInfo: any; plans: any[]; initial: Partial<TrialForm>;
    /** Where it was opened from (startTrialInPlace), sent with the request. */
    origin: string;
  } | null>(null);
  const [trialNotice, setTrialNotice] = useState('');
  /** The plans shown once, right after onboarding. Not a paywall — nothing is
   *  locked — so it renders the same modal without a lockedFeature. */
  const [introPricing, setIntroPricing] = useState(false);
  /** Trial vs paid: the trial row does not count as a purchase, so someone on
   *  a trial is still offered the plan rather than told it is current. */
  const [onTrial, setOnTrial] = useState(false);
  /** The user id the billing summary was fetched with. Read back from
   *  localStorage at click time instead, the trial button failed with
   *  "Please sign in again" on a fresh account: something between mount and
   *  the click had cleared the stored id, though the account was signed in
   *  and its summary had loaded. The id that loaded the plans is the id. */
  const billingUidRef = useRef('');

  // ---- Upgrade prompts (components/upgrade; admin → Billing → Upgrade prompts)
  const prompts = useUpgradePrompts();
  /** The sign-in prompt's copy (raw templates, filled at render so a first
   *  name that arrives a moment later still makes it in). Only used while the
   *  sheet is open BECAUSE of that prompt — see the PricingModal below. */
  const [signinIntro, setSigninIntro] = useState<{ headline: string; message: string } | null>(null);
  // Who is looking, for every prompt: the billing state is the whole rule
  // (explorer / trial_active / trial_lapsed prompt; voyager and assigned do not).
  // ends_at feeds the trial card's countdown ({time_left}).
  useEffect(() => {
    setUpgradeViewer(upgradeState, upgradeTrialLifecycle?.days_left ?? null, profileData.firstName,
      upgradeTrialLifecycle?.ends_at ?? null);
  }, [upgradeState, upgradeTrialLifecycle?.days_left, upgradeTrialLifecycle?.ends_at, profileData.firstName]);
  // The copy guard asks whether this account may copy tech-card text anyway
  // (admins, listed tags / addresses); signing out puts the protection back.
  useEffect(() => {
    setCopyViewer(apiService.getStoredUserId());
    return () => setCopyViewer(null);
  }, [loggedInEmail]);
  /** Re-read the billing state after a purchase, so the prompts step aside
   *  at once instead of asking a new customer to upgrade until they reload.
   *  The summary is taken in by the BILLING_SUMMARY_EVENT listener below. */
  const reloadBillingState = async () => {
    const uid = billingUidRef.current || apiService.getStoredUserId() || '';
    if (!uid) return;
    await apiService.getBillingSummary(uid);
  };
  // Every billing summary that loads for this account — this page's own, the
  // Billing tab's, the checkout's re-checks — updates the prompts, so they can
  // never go on offering the plan to someone the Billing tab shows as a member.
  const upgradeStateRef = useRef<BillingState | null>(null);
  useEffect(() => {
    const PAID: (BillingState | null)[] = ['voyager', 'voyager_assigned'];
    const on = (e: Event) => {
      const { userId, data } = ((e as CustomEvent).detail || {}) as { userId?: string; data?: any };
      const uid = billingUidRef.current || apiService.getStoredUserId() || '';
      if (!data || !userId || userId !== uid) return;
      const was = upgradeStateRef.current;
      const now: BillingState | null = data.state || null;
      upgradeStateRef.current = now;
      const sub = data.subscription;
      setUpgradeTrial(data.trial || null);
      setUpgradeState(now);
      setUpgradeTrialLifecycle(data.trial_lifecycle || null);
      setOnTrial(!!(sub && (sub.is_trial || String(sub.source || '').startsWith('trial'))));
      setCurrentPlanCode(sub && (sub.status === 'active' || sub.status === 'cancelled')
        ? sub.plan_code : 'explorer');
      // Became a member (or stopped being one) since the last summary: the
      // locks follow, not only the prompts.
      if (was && now && PAID.includes(was) !== PAID.includes(now)) void upgrade.refresh();
    };
    window.addEventListener(BILLING_SUMMARY_EVENT, on);
    return () => window.removeEventListener(BILLING_SUMMARY_EVENT, on);
    // Reads refs, setters and upgrade.refresh (stable) only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A tab left open for a while (a phone keeps them for days) asks again when
  // it comes back into view: the plan may have changed in the meantime.
  useEffect(() => {
    let hiddenAt = 0;
    const on = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt > 60_000) void reloadBillingState();
      hiddenAt = 0;
    };
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A purchase or a trial started anywhere else (the Billing tab) — see
  // refreshEntitlements.
  useEffect(() => {
    const on = () => { void reloadBillingState(); };
    window.addEventListener(BILLING_CHANGED_EVENT, on);
    return () => window.removeEventListener(BILLING_CHANGED_EVENT, on);
    // reloadBillingState reads refs and setters only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // The sign-in prompt: the plans sheet with its own headline, once per
  // sign-in (or daily / weekly), for the audiences the admin picked.
  useEffect(() => {
    const cfg = prompts.config.signin_modal;
    const aud = prompts.viewer.audience;
    if (!prompts.loaded || !aud || !cfg.enabled || !cfg.audiences.includes(aud)) return;
    if (upgradePlans.length === 0) return;          // the sheet cannot render without plans
    const uid = billingUidRef.current || apiService.getStoredUserId() || '';
    if (!signinModalDue(uid, cfg.frequency)) return;
    // A brand-new account is being onboarded, and the plans follow
    // onboarding anyway (introPricing, flow v1): that is this sign-in's
    // prompt. Flow v2 opens nothing on entry at all (spec 13 Oct 2026).
    if (showOnboarding) { markSigninModalShown(uid); return; }
    // Never over another sheet, or over the page a deep link brought them to.
    if (deepLinkLanded || upgrade.lockedFeature || introPricing || trialForm || activeModal) return;
    const t = window.setTimeout(() => {
      // openPlans, not gate: nothing was blocked, so no paywall_shown.
      setSigninIntro({ headline: cfg.headline, message: cfg.message });
      if (!upgrade.openPlans(promptFeature('signin_modal'))) { setSigninIntro(null); return; }
      markSigninModalShown(uid);
      trackUpgradePrompt('shown', 'signin_modal', aud, { frequency: cfg.frequency });
    }, 1200);
    return () => window.clearTimeout(t);
    // upgrade.openPlans is stable; the rest are the conditions above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompts.loaded, prompts.viewer, prompts.config, upgradePlans.length, showOnboarding,
      deepLinkLanded, upgrade.lockedFeature, introPricing, trialForm, activeModal]);

  const [profileCompletionPercentage, setProfileCompletionPercentage] = useState(100);
  // Track last query and files for extensive search re-run
  const [lastQuery, setLastQuery] = useState<string>('');
  const [lastFiles, setLastFiles] = useState<File[]>([]);
  const [lastExtensiveSearch, setLastExtensiveSearch] = useState<boolean>(false);
  // Track if current loading is for extensive search
  const [isExtensiveSearchLoading, setIsExtensiveSearchLoading] = useState<boolean>(false);
  // Track message IDs that were just created in the current session (for isFreshMessage logic)
  const [freshMessageIds, setFreshMessageIds] = useState<Set<string>>(new Set());
  
  // Streaming state for live status updates
  const [streamingStatus, setStreamingStatus] = useState<import('./services/apiService').StreamStatus | null>(null);
  const [streamingMessage, setStreamingMessage] = useState<string | null>(null);
  const [streamingResultsCount, setStreamingResultsCount] = useState<number>(0);
  
  // Logout confirmation popup state
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  
  // Password setup toast
  const [userHasPassword, setUserHasPassword] = useState(true);
  const [showPasswordToast, setShowPasswordToast] = useState(false);
  // "This account has no password" is true from the moment the profile loads,
  // but it must wait for onboarding to finish before it is allowed on screen.
  const [passwordToastPending, setPasswordToastPending] = useState(false);

  /** Let the "set a password" toast through, now that onboarding is out of the
   *  way. Safe to call when nothing is pending — it only acts if it is. */
  const releasePasswordToast = React.useCallback(() => {
    setPasswordToastPending(pending => {
      if (pending) setTimeout(() => setShowPasswordToast(true), 1200);
      return false;
    });
  }, []);

  // Accounts that never see onboarding (returning users) must still get the
  // toast — otherwise deferring it would silently mean "never".
  useEffect(() => {
    if (passwordToastPending && !showOnboarding) releasePasswordToast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordToastPending, showOnboarding]);
  
  // Track navigation history for back button handling
  const navigationHistoryRef = useRef<string[]>(['cosmos']);
  
  // Ref to track current view for back button handler
  const activeViewRef = useRef(activeView);
  // When user confirms context break, we create a new chat and switch; stream result goes to this chat
  const streamTargetChatIdRef = useRef<string | null>(null);
  
  // Inactivity: warn at 30 minutes idle, sign out at 2 hours idle.
  const {
    showInactivityPopup,
    remainingSeconds,
    handleStayLoggedIn,
    handleLogout: handleInactivityLogout,
  } = useInactivityTimeout({
    timeoutMinutes: 120,
    warnAfterMinutes: 30,
    onTimeout: onLogout,
    enabled: !!loggedInEmail, // Only enable when logged in
  });

  const recalculateProfileCompletion = useCallback((pd: ProfileData, _rd: RDProfileData) => {
    const fieldMap: Record<string, any> = {
      firstName: pd.firstName, lastName: pd.lastName, email: pd.email,
      contactNumber: pd.contactNumber, city: pd.city, country: pd.country,
      companyName: pd.companyName, designation: pd.designation,
      companyWebsite: pd.companyWebsite, qualification: pd.qualification,
      linkedin: pd.linkedin, hats: pd.hats, intent: pd.intent,
    };
    let score = 0;
    for (const [key, val] of Object.entries(fieldMap)) {
      const w = PROFILE_WEIGHTS[key] || 0;
      if (Array.isArray(val) ? val.length > 0 : (typeof val === 'string' && val.trim() !== '')) {
        score += w;
      }
    }
    // Weights are already in percentage units (max 120%), so score IS the percentage
    setProfileCompletionPercentage(score);
    return score;
  }, []);

  const isContactReady = useCallback((pd: ProfileData, rd: RDProfileData) => {
    return recalculateProfileCompletion(pd, rd) >= 100;
  }, [recalculateProfileCompletion]);

  const checkChatLimits = useCallback(() => {
    // Chat is always allowed — profile completion only gates "contact university"
    return { canChat: true, reason: '' };
  }, []);

  useEffect(() => {
    recalculateProfileCompletion(profileData, rdProfileData);
  }, [profileData, rdProfileData, recalculateProfileCompletion]);

  const closeSidebars = () => {
    setIsLeftSidebarOpen(false);
    setIsRightSidebarOpen(false);
  };

  // Load saved states for inbox modals
  const loadInboxSavedStates = async (assetType: string, assetId: string) => {
    try {
      const userId = apiService.getStoredUserId();
      if (!userId) return {bookmarked: false, liked: null};
      
      // Load saved and liked status
      const [savedResponse, likedResponse] = await Promise.all([
        apiService.getSavedCards(userId, assetType),
        apiService.getLikedCards(userId, assetType)
      ]);
      
      let bookmarked = false;
      let liked = null;
      
      if (savedResponse.success && savedResponse.data?.items) {
        const savedItem = savedResponse.data.items.find((item: any) => item.asset_id === assetId);
        bookmarked = !!savedItem;
      }
      
      if (likedResponse.success && likedResponse.data?.items) {
        const likedItem = likedResponse.data.items.find((item: any) => item.asset_id === assetId);
        liked = likedItem ? likedItem.liked : null;
      }
      
      const state = {bookmarked, liked};
      setInboxSavedStates(prev => new Map(prev).set(assetId, state));
      return state;
    } catch (error) {
      console.error('Error loading inbox saved states:', error);
      return {bookmarked: false, liked: null};
    }
  };

  const handleViewChange = (view: string) => {
    // Track navigation history
    if (navigationHistoryRef.current[navigationHistoryRef.current.length - 1] !== view) {
      navigationHistoryRef.current.push(view);
      // Keep history limited to prevent memory issues
      if (navigationHistoryRef.current.length > 50) {
        navigationHistoryRef.current = navigationHistoryRef.current.slice(-50);
      }
    }
    setActiveView(view);
    closeSidebars();
  };
  
  // Handle logout confirmation
  const handleLogoutConfirm = () => {
    setShowLogoutConfirmation(false);
    if (onLogout) {
      onLogout();
    }
  };
  
  const handleLogoutCancel = () => {
    setShowLogoutConfirmation(false);
  };
  
  // Navigate back — go to the user's primary view, or show the logout popup
  // if they're already on it. Direct Search is the primary surface for
  // everyone now (Cosmos is hidden in the nav), so back-nav lands there.
  const handleBackNavigation = useCallback(() => {
    const primaryView = canSeeDirectSearch(loggedInEmail) ? 'directsearch' : 'cosmos';
    const currentView = activeViewRef.current;
    if (currentView === primaryView) {
      setShowLogoutConfirmation(true);
    } else {
      setActiveView(primaryView);
    }
  }, [loggedInEmail]);

  // Feature access answered: follow a held deep link to What's New / OCEAN
  // when the account may open it (and nothing was opened meanwhile), else stay
  // on the usual home. What's New also closes if it stops being enabled while
  // open; the Ocean view always exists (Coming Soon when OCEAN is not enabled).
  const initialViewRef = useRef(activeView);
  useEffect(() => {
    if (!access.loaded || !pendingGatedView) return;
    const feature = GATED_VIEW_FEATURE[pendingGatedView];
    if (access.features[feature].enabled && activeViewRef.current === initialViewRef.current) {
      handleViewChange(pendingGatedView);
      setGatedLinkFollowed(true);
    }
    setPendingGatedView(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.loaded, pendingGatedView]);
  useEffect(() => {
    if (access.loaded && activeView === 'whatsnew' && !access.features.whatsnew.enabled) {
      setActiveView(canSeeDirectSearch(loggedInEmail) ? 'directsearch' : 'cosmos');
    }
  }, [access, activeView, loggedInEmail]);

  const handleNavigateToProfileTab = (tab: string = 'profile') => {
    setProfileInitialTab(tab);
    setActiveView('profile');
    closeSidebars();
  };


  const openTechCardModal = () => setTechCardModalOpen(true);
  // Where the Contact University sheet was opened from — 'search_results'
  // (Direct Search list card) or 'tech_modal' (the technology window). Sent
  // with the contacts request and the Talk to Concierge lead
  // (Admin → Leads → Contact University / Talk to Concierge).
  const [contactSurface, setContactSurface] = useState('');
  // "Contact University" on an accordion card (Direct Search list, an
  // Explorer's Cosmos picks — 'cosmos_picks' — or the modal): the legacy
  // card's gate, then the same ContactTTO modal.
  const contactFromCard = (card: { id: string; title: string; university: string; country: string },
                           surface: 'search_results' | 'tech_modal' | 'cosmos_picks') => {
    setSelectedCardData((prev: any) => (prev && (prev.utech_technology_id === card.id || prev.id === card.id)) ? prev : {
      id: card.id, utech_technology_id: card.id, title: card.title, institution: card.university, location: card.country,
    });
    setContactSurface(surface);
    upgrade.gate(!!upgrade.ent.can_contact, 'university contact details',
      () => openModal(USE_TTO_CONTACT_MODAL ? ModalType.ContactTTO : ModalType.ContactUniversity), 'can_contact',
      { control: 'contact', tech_id: card.id, surface });
  };
  /** The technologies behind the one in the card modal, when the reader
   *  moved on through Similar Technologies — Back returns to the last one.
   *  Closing the modal forgets them. */
  const [techModalTrail, setTechModalTrail] = useState<Array<{ id: string; title: string }>>([]);
  const closeTechCardModal = () => { setTechCardModalOpen(false); setTechModalTrail([]); };
  const openResearcherCardModal = () => setResearcherCardModalOpen(true);
  const closeResearcherCardModal = () => setResearcherCardModalOpen(false);

  // `relevance` is the score the RESULT actually carried (the hybrid
  // cross-encoder rerank score, or the LLM relevance score when the
  // fallback re-ranked). Passing it through is what stopped every card
  // reading a hardcoded 85%.
  const handleReadMore = async (type: SavedCardType, id: string, relevance?: number) => {
    setCardRelevance(typeof relevance === 'number' && isFinite(relevance) ? relevance : null);
    setSelectedCardId(id);
    setCardDataLoading(true);
    setSelectedCardData(null);
    
    // Get message status for inbox context
    const getMessageStatus = () => {
      if (activeView === 'inbox') {
        // Find the conversation for this tech card
        const conversation = inboxConversations.find(conv => conv.techCardId === id);
        if (conversation) {
          // Determine status based on conversation data
          const status = conversation.status?.toLowerCase() || '';
          const messageCount = conversation.messagePreview.match(/(\d+) message/)?.[1] || '0';
          const count = parseInt(messageCount);
          
          console.log(`Status for conversation ${conversation.id}: "${status}", message count: ${count}`);
          
          // Logic for determining message status based on email tracking
          if (status.includes('bounced') || status.includes('failed')) {
            return 'Delivery failed';
          } else if (status.includes('opened') || status.includes('read')) {
            if (count === 1) {
              return 'Message read';
            } else {
              return 'Reply pending';
            }
          } else if (status.includes('clicked')) {
            return 'Message clicked';
          } else if (status.includes('delivered') || status.includes('sent')) {
            if (count === 1) {
              return 'Message sent';
            } else {
              return 'Awaiting reply';
            }
          } else if (count > 1) {
            return 'Reply received';
          } else {
            // Default based on message count when no specific status
            return 'Message sent';
          }
        }
      }
      // Return appropriate label based on context
      if (activeView === 'foryou') {
        return 'Technology Card'; // For You page should show 'Technology Card'
      }
      // The real score from the result. A card opened without one (no
      // ranking, or a filter-only search) shows no relevance badge rather
      // than a made-up number.
      return typeof relevance === 'number' && isFinite(relevance)
        ? Math.round(relevance)
        : null;
    };
    
    try {
    if (type === SavedCardType.Tech) {
        // Load saved states before opening modal
        await loadInboxSavedStates('technology', id);
        
        const response = await apiService.getTechnologyDetails(id);
        console.log(`Tech API response for ${id}:`, response);

        const messageStatus = getMessageStatus();

        if (response.success && response.data) {
          // IP summary / publications summary live inside the ip_information
          // JSON. The backend now also surfaces them at the top level
          // (response.data.ip_summary / .publications_summary), but we keep a
          // defensive fallback into ip_information so this still resolves even
          // for older/other response shapes. (Fix: Algolia card IP/pubs null bug.)
          const ipInfoObj = (() => {
            const raw = response.data.ip_information;
            if (!raw) return {} as any;
            if (typeof raw === 'string') {
              try { return JSON.parse(raw); } catch { return {} as any; }
            }
            return raw as any;
          })();
          const resolvedIpSummary =
            response.data.ip_summary || ipInfoObj?.ip_summary || 'No IP summary available';
          const resolvedPublicationsSummary =
            response.data.publications_summary || ipInfoObj?.publications_summary || 'No publications summary available';

          setSelectedCardData({
            id: response.data.utech_technology_id || id,
            utech_technology_id: response.data.utech_technology_id || id, // Include for API calls
            reference_number: response.data.reference_number || '', // Add reference number for contact modal
            relevanceScore: messageStatus, // Use message status for inbox, relevance score for others
            title: response.data.utech_title || response.data.title || `Technology ${id}`,
            institution: response.data.university_name || '',
            location: response.data.university_country || '',
            trl: response.data.trl || 'TRL 5', // Use the properly formatted TRL from API
            trlReasoning: response.data.trl_reasoning, // Add TRL reasoning from API
            researchers: (() => {
              const cleaned = cleanResearchers(response.data.researchers);
              return cleaned.length > 0 ? cleaned : [{ name: 'Researcher', url: '' }];
            })(),
            summary: response.data.utech_summary || response.data.abstract || response.data.summary || 'Technology details not available',
            ipSummary: resolvedIpSummary,
            publicationsSummary: resolvedPublicationsSummary
          });
      openTechCardModal();
    } else {
          console.error('Failed to fetch technology details:', response.error);
          console.log('Using minimal tech card data for ID:', id);
          
          // Create minimal card data instead of using dummy data
          setSelectedCardData({
            id: id,
            utech_technology_id: id,
            relevanceScore: messageStatus, // Use message status for inbox, relevance score for others
            title: `Technology ${id}`,
            institution: 'University',
            location: 'Unknown Location',
            trl: 'TRL 5',
            researchers: [{ name: 'Researcher', url: '' }],
            summary: 'Technology details are being loaded...',
            ipSummary: 'No IP summary available',
            publicationsSummary: 'No publications summary available'
          });
          openTechCardModal();
        }
      } else {
        // Load saved states before opening modal
        await loadInboxSavedStates('researcher', id);
        
        const response = await apiService.getResearcherDetails(id);
        console.log(`Researcher API response for ${id}:`, response);
        
        const messageStatus = getMessageStatus();
        
        if (response.success && response.data) {
          setSelectedCardData({
            utech_researcher_id: response.data.utech_researcher_id || id, // Include for API calls
            relevanceScore: messageStatus, // Use message status for inbox, relevance score for others
            title: response.data.researcher_name || response.data.name || `Researcher ${id}`,
            institution: response.data.university_name || 'Unknown University',
            location: response.data.university_country || 'Unknown Location',
            techs: response.data.techs || [], // Would need additional API call to get associated technologies
            topics: response.data.topics || []
          });
          openResearcherCardModal();
        } else {
          console.error('Failed to fetch researcher details:', response.error);
          console.log('Using minimal researcher card data for ID:', id);
          
          // Create minimal card data instead of using dummy data
          setSelectedCardData({
            utech_researcher_id: id,
            relevanceScore: messageStatus, // Use message status for inbox, relevance score for others
            title: `Researcher ${id}`,
            institution: 'University',
            location: 'Unknown Location',
            techs: [],
            topics: []
          });
      openResearcherCardModal();
        }
      }
    } catch (error) {
      console.error('Error fetching card data:', error);
      console.log('Using minimal fallback data for ID:', id, 'Type:', type);
      
      const messageStatus = getMessageStatus();
      
      // Create minimal fallback data instead of using dummy data
      if (type === SavedCardType.Tech) {
        setSelectedCardData({
          id: id,
          utech_technology_id: id,
          relevanceScore: messageStatus, // Use message status for inbox, relevance score for others
          title: `Technology ${id}`,
          institution: 'University',
          location: 'Unknown Location',
          trl: 'TRL 5',
          researchers: [{ name: 'Researcher', url: '' }],
          summary: 'Unable to load technology details',
          ipSummary: 'No IP summary available',
          publicationsSummary: 'No publications summary available'
        });
        openTechCardModal();
      } else {
        setSelectedCardData({
          utech_researcher_id: id,
          relevanceScore: messageStatus, // Use message status for inbox, relevance score for others
          title: `Researcher ${id}`,
          institution: 'University',
          location: 'Unknown Location',
          techs: [],
          topics: []
        });
        openResearcherCardModal();
      }
    } finally {
      setCardDataLoading(false);
    }
  };


  const handleAddNewChat = (options: { navigateToCosmos?: boolean } = {}) => {
    // `navigateToCosmos` defaults to true so user-initiated "New chat" clicks
    // jump to the chat surface as expected. Initialization paths (mount-time
    // session restore, hydrating chats from sessionStorage) pass `false` so
    // they don't yank a user off Direct Search just because chats exist.
    const navigateToCosmos = options.navigateToCosmos !== false;
    // Log current chat ID for verification (should be none or blank)
    console.log('🆕 handleAddNewChat called - Current activeChatId:', activeChatId || 'none/blank', 'navigateToCosmos:', navigateToCosmos);

    // Check chat limits for incomplete profiles
    const chatLimitCheck = checkChatLimits();
    if (!chatLimitCheck.canChat) {
      // Create a new chat with a system message about the limit
      const newChatId = Date.now().toString();
      const newChat: Chat = {
          id: newChatId,
          title: 'Chat Limit Reached',
          date: 'Just now'
      };

      const systemMessage: ChatMessage = {
        id: Date.now().toString(),
        text: "You have reached the chat limit for incomplete profiles. Please complete your profile to continue using the chat functionality.",
        sender: Sender.AI
      };

      setChats(prev => [newChat, ...prev]);
      setMessages(prev => ({...prev, [newChatId]: [systemMessage]}));
      setActiveChatId(newChatId);
      if (navigateToCosmos) setActiveView('cosmos');
      closeSidebars();

      console.log('📝 Created new chat (limit reached) - Chat ID:', newChatId, 'Title:', newChat.title);

      // Redirect to profile page after a short delay
      if (navigateToCosmos) {
        setTimeout(() => {
          setActiveView('profile');
        }, 2000);
      }
      return;
    }

    const newChatId = Date.now().toString();
    const newChat: Chat = {
        id: newChatId,
        title: 'New Chat',
        date: 'Just now'
    };
    setChats(prev => [newChat, ...prev]);
    setMessages(prev => ({...prev, [newChatId]: []}));
    setActiveChatId(newChatId);
    if (navigateToCosmos) setActiveView('cosmos');
    closeSidebars();

    console.log('📝 Created new chat - Chat ID:', newChatId, 'Title:', newChat.title);
  };

  const handleSelectChat = async (chatId: string) => {
    // Disable chat switching when a search is running
    if (isLoading) {
      return;
    }
    
    // Find the chat title for logging
    const selectedChat = chats.find(chat => chat.id === chatId);
    const chatTitle = selectedChat?.title || 'Unknown';
    
    // Log chat switching with ID and title
    console.log('🔄 Switching chat - Chat ID:', chatId, 'Title:', chatTitle);
    
    // Clear fresh message IDs when switching chats to prevent stale UI state
    setFreshMessageIds(new Set());
    
    // Clear last query/files state for the new chat context
    setLastQuery('');
    setLastFiles([]);
    setLastExtensiveSearch(false);
    
    setActiveChatId(chatId);
    closeSidebars();
    
    // Store the session_id for this chat in sessionStorage
    // The chatId is the chat_session_id from the database
    sessionStorage.setItem(`chat_session_${chatId}`, chatId);
    
    // Always reload messages when switching chats to ensure fresh data
    // This prevents stale cache issues and ensures chat history is synced
    setIsChatLoading(true);
    try {
      const response = await apiService.getChatMessages(chatId);
      if (response.success && response.data) {
        // The API returns the array directly, not wrapped in a messages property
        const messagesArray = Array.isArray(response.data) ? response.data : (response.data as any)?.messages || [];
        
        // Map API messages to our ChatMessage interface
        const apiMessages: ChatMessage[] = messagesArray.map((msg: any) => ({
          id: msg.message_id || msg.id || Date.now().toString(),
          text: msg.text || msg.message || msg.content || '',
          sender: msg.sender === 'user' ? Sender.User : Sender.AI,
          attachments: msg.attachments || [],
          dbMessageId: msg.message_id || msg.id, // Use database message ID for feedback
          extensiveSearch: msg.extensiveSearch || false // Preserve extensive search flag
        }));
        
        setMessages(prev => ({
          ...prev,
          [chatId]: apiMessages
        }));
        
        console.log(`✅ Loaded ${apiMessages.length} messages for chat ${chatId} (${chatTitle})`);
      } else {
        // Initialize with empty array if no messages returned
        setMessages(prev => ({
          ...prev,
          [chatId]: []
        }));
        console.log(`✅ Initialized empty chat ${chatId} (${chatTitle})`);
      }
    } catch (error) {
      console.error('Failed to load chat messages:', error);
      // Initialize with empty array if loading fails
      setMessages(prev => ({
        ...prev,
        [chatId]: []
      }));
    } finally {
      setIsChatLoading(false);
    }
  };

  // Function to refresh chat list from API
  const refreshChatList = async () => {
    if (loggedInEmail) {
      const userId = apiService.getStoredUserId();
      if (userId) {
        try {
          const response = await apiService.getUserChats(userId);
          if (response.success && response.data) {
            const chatsArray = Array.isArray(response.data) ? response.data : (response.data as any)?.chats || [];
            
            const apiChats: Chat[] = chatsArray.map((chat: any) => ({
              id: chat.chat_session_id || chat.session_id || chat.id,
              title: chat.title || 'Untitled Chat',
              date: chat.created_at ? new Date(chat.created_at).toLocaleDateString() : 'Recently'
            }));
            
            setChats(apiChats);
          }
        } catch (error) {
          console.error('Failed to refresh chat list:', error);
        }
      }
    }
  };

  // Function to refresh profile data from API
  const refreshProfileData = async () => {
    const userId = apiService.getStoredUserId();
    if (userId) {
      try {
        const response = await apiService.getProfile(userId);
        if (response.success && response.data) {
          const profile = response.data;
          const updatedProfileData: ProfileData = {
            email: profile.user.email,
            firstName: profile.user.first_name || '',
            lastName: profile.user.last_name || '',
            contactNumber: profile.user.phone || '',
            countryCode: '91', // Default
            country: profile.user.country || '',
            city: profile.user.city || '',
            companyName: profile.profile.organisation_name || '',
            designation: profile.profile.designation || '',
            companyWebsite: profile.profile.company_website_url || '',
            qualification: profile.profile.highest_qualification || '',
            linkedin: profile.profile.linkedin_profile_url || '',
            summary: profile.profile.professional_summary || '',
            hats: profile.profile.hats || [],
            intent: profile.profile.intent || [],
            profileImage: profile.user.profile_picture_url || undefined,
          };
          setProfileData(updatedProfileData);

          // Also refresh interests data
          const interestsData: InterestsData = {
            keywords: profile.profile.interests || [],
            emailAlerts: profile.profile.alerts_enabled || false,
          };
          setInterestsData(interestsData);
          
          console.log('✅ Profile and interests data refreshed');
        }
      } catch (error) {
        console.error('Failed to refresh profile data:', error);
      }
    }
  };

  // Load profile data from API on login
  useEffect(() => {
    const loadProfileFromAPI = async () => {
      if (loggedInEmail && !isProfileLoaded) {
        const userId = apiService.getStoredUserId();
        if (userId) {
          try {
            const response = await apiService.getProfile(userId);
            if (response.success && response.data) {
              const profile = response.data;
              const profileData: ProfileData = {
                email: profile.user.email,
                firstName: profile.user.first_name || '',
                lastName: profile.user.last_name || '',
                contactNumber: profile.user.phone || '',
                countryCode: '91', // Default
                country: profile.user.country || '',
                city: profile.user.city || '',
                companyName: profile.profile.organisation_name || '',
                designation: profile.profile.designation || '',
                companyWebsite: profile.profile.company_website_url || '',
                qualification: profile.profile.highest_qualification || '',
                linkedin: profile.profile.linkedin_profile_url || '',
                summary: profile.profile.professional_summary || '',
                hats: profile.profile.hats || [],
                intent: profile.profile.intent || [],
                profileImage: profile.user.profile_picture_url || undefined,
              };
              setProfileData(profileData);

              // Update R&D profile data
              const rdData: RDProfileData = {
                collaborationTypes: profile.profile.collaboration_types || [],
                collaborationLead: profile.profile.collaboration_lead || [],
                collaborationTimeline: profile.profile.collaboration_timeline || 'Select one',
                rdBudget: profile.profile.rd_budget || 'Select one',
                otherInfo: profile.profile.other_info || '',
                corporateDeckUrl: '',
                rdSummary: profile.profile.rd_summary || '',
                corporateDeckFile: '',
              };
              setRDProfileData(rdData);

              // Update interests data
              const interestsData: InterestsData = {
                keywords: profile.profile.interests || [],
                emailAlerts: profile.profile.alerts_enabled || false,
              };
              setInterestsData(interestsData);

              // Calculate profile completion percentage using the new function
              const averageCompletion = recalculateProfileCompletion(profileData, rdProfileData);
              
              setIsProfileLoaded(true);
              
              // Check if user has a password set — show toast if not
              const passwordSet = profile.user.has_password ?? true;
              setUserHasPassword(passwordSet);
              // Deliberately NOT shown during onboarding. It used to fire 1.5s
              // after load, so a new user got "set a password" on top of the
              // industry/keyword questions, every reload. The onboarding effect
              // raises it once the user is actually inside Cosmos.
              setPasswordToastPending(!passwordSet);
              
              // Show popup if profile is incomplete (delay for better UX)
              // Only show popup after profile data is fully loaded from API
              if (averageCompletion < 100) {
                setTimeout(() => {
                  setShowProfileCompletionPopup(true);
                }, 2000); // Show after 2 seconds
              }

              // Preload keywords cache for autocomplete functionality
              keywordsCache.preloadKeywords().catch(error => {
                console.warn('Keywords preload failed:', error);
              });

              // Background loading of personalized recommendations if user has 5+ interests
              if (interestsData.keywords.length >= MIN_INTERESTS_FOR_RECOMMENDATIONS) {
                // Use the dedicated preload method for background loading
                apiService.preloadRecommendations(userId);
              } else {
                console.log(`ℹ️ User has ${interestsData.keywords.length} interests, need 5+ for recommendations`);
              }
          }
        } catch (error) {
          console.error('Failed to load profile from API:', error);
          setIsProfileLoaded(true); // Mark as loaded even on error to prevent retry loops
        }
      }
    }
  };

  loadProfileFromAPI();
}, [loggedInEmail, isProfileLoaded]);

// Track when initial loading is complete
useEffect(() => {
  if (isProfileLoaded && isChatsLoaded) {
    setIsInitialLoading(false);
  }
}, [isProfileLoaded, isChatsLoaded]);

  // Load user chats from API on login
  useEffect(() => {
    const loadChatsFromAPI = async () => {
      if (loggedInEmail && !isChatsLoaded) {
        const userId = apiService.getStoredUserId();
        if (userId) {
          try {
            // Log current chat ID for verification (should be none or blank)
            console.log('📥 Loading chats from API - Current activeChatId:', activeChatId || 'none/blank');
            
            const response = await apiService.getUserChats(userId);
            if (response.success && response.data) {
              // The API returns the array directly, not wrapped in a chats property
              const chatsArray = Array.isArray(response.data) ? response.data : (response.data as any)?.chats || [];
              
              // Map API chats to our Chat interface
              const apiChats: Chat[] = chatsArray.map((chat: any) => ({
                id: chat.chat_session_id || chat.session_id || chat.id,
                title: chat.title || 'Untitled Chat',
                date: chat.created_at ? new Date(chat.created_at).toLocaleDateString() : 'Recently'
              }));
              
              setChats(apiChats);
              setIsChatsLoaded(true);
              
              console.log(`📋 Loaded ${apiChats.length} chats from API`);
            } else {
              // Even if API call fails or returns no chats, mark as loaded
              setIsChatsLoaded(true);
            }
          } catch (error) {
            console.error('Failed to load chats:', error);
            setIsChatsLoaded(true); // Set to true to prevent retrying
          }
        }
      }
    };

    loadChatsFromAPI();
  }, [loggedInEmail, isChatsLoaded]);

  // Reset initialization flag when user changes
  useEffect(() => {
    hasInitializedChat.current = false;
  }, [loggedInEmail]);

  // Always trigger add chat by default when chats are loaded and no active chat exists.
  // Guarded by activeView === 'cosmos' so users on Direct Search aren't yanked back.
  useEffect(() => {
    if (loggedInEmail && isChatsLoaded && !activeChatId && activeView === 'cosmos' && !hasInitializedChat.current) {
      console.log('🆕 Chats loaded, no active chat ID found - triggering add chat by default');
      hasInitializedChat.current = true;
      handleAddNewChat({ navigateToCosmos: false });
    }
  }, [loggedInEmail, isChatsLoaded, activeChatId, activeView]);

  useEffect(() => {
    try {
      const userKey = loggedInEmail || 'default';
      const savedChats = sessionStorage.getItem(`chats_${userKey}`);
      const savedMessages = sessionStorage.getItem(`messages_${userKey}`);
      const savedActiveChatId = sessionStorage.getItem(`activeChatId_${userKey}`);
      const savedInboxConversations = sessionStorage.getItem(`inboxConversations_${userKey}`);
      const savedActiveConversationId = sessionStorage.getItem(`activeConversationId_${userKey}`);
      if (savedInboxConversations) {
          const parsedConvos = JSON.parse(savedInboxConversations);
          if (Array.isArray(parsedConvos)) {
              setInboxConversations(parsedConvos);
              if (savedActiveConversationId && parsedConvos.some(c => c.id === savedActiveConversationId)) {
                  setActiveConversationId(savedActiveConversationId);
              } else if (parsedConvos.length > 0) {
                  setActiveConversationId(parsedConvos[0].id);
              }
          }
      }

      if (savedChats && savedMessages) {
          const parsedChats = JSON.parse(savedChats);
          if (Array.isArray(parsedChats) && parsedChats.length > 0) {
            setChats(parsedChats);
            setMessages(JSON.parse(savedMessages));
            // Always start with a fresh chat on login instead of loading the last active chat
            handleAddNewChat({ navigateToCosmos: false });
          } else {
            handleAddNewChat({ navigateToCosmos: false });
          }
      } else {
          handleAddNewChat({ navigateToCosmos: false });
      }
    } catch (error) {
      console.error("Failed to parse from session storage", error);
      handleAddNewChat({ navigateToCosmos: false });
    }
  }, [loggedInEmail]);

  useEffect(() => {
    const userKey = loggedInEmail || 'default';
    sessionStorage.setItem(`chats_${userKey}`, JSON.stringify(chats));
    sessionStorage.setItem(`messages_${userKey}`, JSON.stringify(messages));
    if(activeChatId) sessionStorage.setItem(`activeChatId_${userKey}`, activeChatId);
    sessionStorage.setItem(`userProfileData_${userKey}`, JSON.stringify(profileData));
    sessionStorage.setItem(`userRDProfileData_${userKey}`, JSON.stringify(rdProfileData));
    sessionStorage.setItem(`userInterestsData_${userKey}`, JSON.stringify(interestsData));
    sessionStorage.setItem(`inboxConversations_${userKey}`, JSON.stringify(inboxConversations));
    if(activeConversationId) sessionStorage.setItem(`activeConversationId_${userKey}`, activeConversationId);
  }, [chats, messages, activeChatId, profileData, rdProfileData, interestsData, inboxConversations, activeConversationId, loggedInEmail]);
  
  // Removed automatic Gemini reply logic - no longer needed for real email conversations
  
  // Handle browser back button - runs once on mount
  useEffect(() => {
    // Clear any OAuth URLs from history by replacing current state
    // This prevents back button from going to LinkedIn/OAuth callback URLs
    window.history.replaceState({ view: 'cosmos', app: 'cosmos-copilot' }, '', window.location.pathname);
    
    // Push a clean state to prevent immediate back navigation
    window.history.pushState({ view: 'cosmos', app: 'cosmos-copilot' }, '', window.location.pathname);
    
    const handlePopState = (event: PopStateEvent) => {
      // Always prevent navigation and show our handler
      handleBackNavigation();
      
      // Push state again to stay on current page
      window.history.pushState({ view: activeViewRef.current, app: 'cosmos-copilot' }, '', window.location.pathname);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []); // Run only once on mount
  
  // Update history state and ref when view changes
  useEffect(() => {
    activeViewRef.current = activeView;
    window.history.replaceState({ view: activeView, app: 'cosmos-copilot' }, '', window.location.pathname);
  }, [activeView]);
  
  useEffect(() => {
    if (activeView === 'cosmos' && activeChatId && (messages[activeChatId] || []).length === 0) {
      // Get user ID for personalized suggestions
      const userId = apiService.getStoredUserId();
      generateSamplePrompts(userId || undefined).then(newPrompts => {
        setSamplePrompts(newPrompts);
      });
    }
  }, [activeChatId, activeView, messages]);

  // Load email conversations when switching to inbox view
  useEffect(() => {
    const loadEmailConversations = async () => {
      if (activeView === 'inbox' && loggedInEmail) {
        setIsInboxLoading(true);
        try {
          const userId = apiService.getStoredUserId();
          if (!userId) return;

          const response = await apiService.getEmailConversations(userId);
          if (response.success && response.data) {
            // API returns {conversations: [...], total: N}
            const conversations = response.data.conversations || [];
            
            // Map API response to InboxConversation format with proper tech titles and contact names
            const emailConversations = await Promise.all(conversations.map(async (conv: any) => {
              let techId = null;
              let techTitle = conv.tech_title || null; // Don't set default fallback immediately
              let actualTechId = conv.tech_id || conv.conversation_id; // Use tech_id from API if available
              
              // Priority 1: Extract tech ID from conversation ID first
              if (conv.conversation_id) {
                let techIdMatch = conv.conversation_id.match(/UTECH-[A-Z]{3}-\d+/i);
                if (!techIdMatch) {
                  techIdMatch = conv.conversation_id.match(/([A-Z]{2,}\-[A-Z]{2,}\-\d+)/i);
                }
                if (techIdMatch) {
                  techId = techIdMatch[0];
                  console.log(`🎯 Extracted tech ID from conversation_id: ${techId}`);
                }
              }
              
              // Priority 2: If not found in conversation ID, try extracting from subject
              if (!techId && conv.subject) {
                let techIdMatch = conv.subject.match(/UTECH-[A-Z]{3}-\d+/i);
                if (!techIdMatch) {
                  techIdMatch = conv.subject.match(/([A-Z]{2,}\-[A-Z]{2,}\-\d+)/i);
                }
                if (techIdMatch) {
                  techId = techIdMatch[0];
                  console.log(`📧 Extracted tech ID from subject: ${techId}`);
                }
              }
              
              console.log(`🔍 Processing conversation: ConversationId="${conv.conversation_id}", Subject="${conv.subject}", ExtractedTechId="${techId}", ConvTechTitle="${conv.tech_title}", ConvTechId="${conv.tech_id}"`);
              
              // If we have a tech ID, fetch the actual technology details
              if (techId) {
                try {
                  const techResponse = await apiService.getTechnologyDetails(techId);
                  if (techResponse.success && techResponse.data) {
                    techTitle = techResponse.data.utech_title || techResponse.data.title || techId;
                    actualTechId = techId;
                    console.log(`✅ Successfully loaded tech details for ${techId}: ${techTitle}`);
                  } else {
                    console.log(`❌ Failed to load tech details for ${techId}:`, techResponse.error);
                    techTitle = techId; // Use tech ID as fallback
                    actualTechId = techId;
                  }
                } catch (error) {
                  console.error('❌ Error fetching tech details for', techId, error);
                  techTitle = techId; // Use tech ID as fallback
                  actualTechId = techId;
                }
              } else {
                // If no tech ID found anywhere, check if conv has direct tech info
                if (conv.tech_title) {
                  techTitle = conv.tech_title;
                }
                if (conv.tech_id) {
                  actualTechId = conv.tech_id;
                }
                console.log(`📝 Using conversation tech info: ${techTitle} (ID: ${actualTechId})`);
              }
              
              // Final fallback if no title was found
              if (!techTitle) {
                techTitle = 'Technology Inquiry';
                console.log(`⚠️ Using fallback title for conversation ${conv.conversation_id}`);
              }

              // Extract contact name from conversation messages
              let contactName = extractParticipantName(conv.participant_email);
              try {
                // Fetch detailed conversation to extract contact name from message content
                const detailResponse = await apiService.getEmailConversation(conv.conversation_id);
                if (detailResponse.success && detailResponse.data && detailResponse.data.messages) {
                  const extractedName = extractContactNameFromContent(detailResponse.data.messages);
                  if (extractedName) {
                    contactName = extractedName;
                  }
                }
              } catch (error) {
                console.error('Failed to extract contact name for', conv.conversation_id, error);
                // Keep the email-based name as fallback
              }
              
              // Create placeholder messages since detailed messages come from individual conversation endpoint
              const placeholderMessages = [{
                id: 'placeholder-1',
                sender: Sender.User,
                content: 'Loading conversation details...',
                timestamp: new Date(conv.last_message_at || conv.updated_at).toLocaleString(),
                status: MessageStatus.Delivered,
                attachments: []
              }];

              return {
                id: conv.conversation_id,
                contactName: contactName, // Use extracted contact name instead of email-based name
                contactAffiliation: 'University Researcher',
                lastMessageTimestamp: new Date(conv.last_message_at || conv.updated_at).toLocaleString(),
                messagePreview: `${conv.message_count} message${conv.message_count !== 1 ? 's' : ''} - Click to view details`,
                techCardId: actualTechId, // Use actual tech ID instead of conversation ID
                techCardTitle: techTitle,
                subject: conv.subject, // Original email subject for replies
                messages: placeholderMessages,
                participantEmail: conv.participant_email,
                participantName: contactName, // Use extracted contact name
                status: conv.status,
                // Store the first message date for contact initiation
                firstMessageDate: conv.created_at || conv.updated_at || conv.last_message_at
              };
            }));

            // Set the conversations first
            setInboxConversations(emailConversations);
            
            // Auto-select the first conversation if none is currently selected
            if (emailConversations.length > 0 && !activeConversationId) {
              const firstConversationId = emailConversations[0].id;
              console.log('Auto-selecting first conversation:', firstConversationId);
              setActiveConversationId(firstConversationId);
            }
          }
        } catch (error) {
          console.error('Failed to load email conversations:', error);
        } finally {
          setIsInboxLoading(false);
        }
      } else {
        setIsInboxLoading(false);
      }
    };

    loadEmailConversations();
  }, [activeView, loggedInEmail]);

  // Auto-load conversation details when activeConversationId changes
  useEffect(() => {
    if (activeConversationId && activeView === 'inbox') {
      // Check if this conversation already has detailed messages (not placeholder)
      const currentConversation = inboxConversations.find(c => c.id === activeConversationId);
      if (currentConversation && currentConversation.messages.some(m => m.content === 'Loading conversation details...')) {
        console.log('Auto-loading details for conversation:', activeConversationId);
        handleSelectConversation(activeConversationId);
      }
    }
  }, [activeConversationId, activeView, inboxConversations]);

  // Listen for inbox refresh trigger
  useEffect(() => {
    const checkInboxRefresh = () => {
      if (localStorage.getItem('refreshInbox') === 'true') {
        localStorage.removeItem('refreshInbox');
        
        // Reload inbox conversations if in inbox view
        if (activeView === 'inbox' && loggedInEmail) {
          const loadEmailConversations = async () => {
            try {
              const userId = apiService.getStoredUserId();
              if (!userId) return;

              const response = await apiService.getEmailConversations(userId);
              if (response.success && response.data) {
                // API returns {conversations: [...], total: N}
                const conversations = response.data.conversations || [];
                
                const emailConversations = await Promise.all(conversations.map(async (conv: any) => {
                  // Prefer the structured `utech_technology_id` field on the
                  // conversation row (set when the send endpoint persists it).
                  // Fall back to parsing the subject for a UTECH-… pattern
                  // (legacy rows) — and the reference-number pattern like
                  // T-676 / UA25-211 is consulted via the conversation_id
                  // slug (`{ref}-{epoch}-{userId}`) as a last resort.
                  const techIdMatch = conv.subject?.match(/UTECH-[A-Z]{3,5}-\d+/i);
                  const techId: string | null =
                    conv.utech_technology_id ||
                    (techIdMatch ? techIdMatch[0] : null);
                  
                  let techTitle = 'Technology Inquiry';
                  let actualTechId = conv.conversation_id; // Fallback
                  
                  // If we have a tech ID, fetch the actual technology details
                  if (techId) {
                    try {
                      const techResponse = await apiService.getTechnologyDetails(techId);
                      if (techResponse.success && techResponse.data) {
                        techTitle = techResponse.data.utech_title || techResponse.data.title || techId;
                        actualTechId = techId;
                      }
                    } catch (error) {
                      console.error('Failed to fetch tech details for', techId, error);
                      techTitle = techId; // Use tech ID as fallback
                      actualTechId = techId;
                    }
                  }
                  
                  // Create placeholder messages since detailed messages come from individual conversation endpoint
                  const placeholderMessages = [{
                    id: 'placeholder-1',
                    sender: Sender.User,
                    content: 'Loading conversation details...',
                    timestamp: new Date(conv.last_message_at || conv.updated_at).toLocaleString(),
                    status: MessageStatus.Delivered,
                    attachments: []
                  }];
                        
                        return {
                    id: conv.conversation_id,
                    contactName: extractParticipantName(conv.participant_email),
                    contactAffiliation: 'University Researcher',
                    lastMessageTimestamp: new Date(conv.last_message_at || conv.updated_at).toLocaleString(),
                    messagePreview: `${conv.message_count} message${conv.message_count !== 1 ? 's' : ''} - Click to view details`,
                    techCardId: actualTechId, // Use actual tech ID instead of conversation ID
                    techCardTitle: techTitle,
                    messages: placeholderMessages,
                    participantEmail: conv.participant_email,
                    participantName: extractParticipantName(conv.participant_email),
                    status: conv.status
                  };
                }));

                setInboxConversations(emailConversations);
              }
            } catch (error) {
              console.error('Failed to reload email conversations:', error);
            }
          };

          loadEmailConversations();
        }
      }
    };

    // Check every second for the refresh flag
    const interval = setInterval(checkInboxRefresh, 1000);
    
    return () => clearInterval(interval);
  }, [activeView, loggedInEmail]);

  // Helper function to extract participant name from email
  const extractParticipantName = (email: string): string => {
    if (email.includes('parse.universitytech.io')) {
      // Extract university name from parse email format
      return 'University Researcher';
    }
    return email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to get message status from events
  const getMessageStatus = (events: any[], messageId: string): MessageStatus => {
    // For simplicity, check if there are any 'open' events after the message
    const openEvents = events.filter(e => e.event_type === 'open');
    if (openEvents.length > 0) {
      return MessageStatus.Read;
    }
    
    const deliveredEvents = events.filter(e => e.event_type === 'delivered');
    if (deliveredEvents.length > 0) {
      return MessageStatus.Delivered;
    }
    
    return MessageStatus.Delivered; // Default status
  };

  const handleSendMessage = async (prompt: string, files: File[] = []) => {
    if (!activeChatId) return;

    // Check chat limits for incomplete profiles
    const chatLimitCheck = checkChatLimits();
    if (!chatLimitCheck.canChat) {
      // Add a system message to the chat instead of alert
      const systemMessage: ChatMessage = {
        id: Date.now().toString(),
        text: "You have reached the chat limit for incomplete profiles. Please complete your profile to continue using the chat functionality.",
        sender: Sender.AI
      };
      
      setMessages(prev => ({
        ...prev,
        [activeChatId]: [...(prev[activeChatId] || []), systemMessage]
      }));
      
      // Redirect to profile page after a short delay
      setTimeout(() => {
        setActiveView('profile');
      }, 2000);
      return;
    }

    const isFirstMessage = (messages[activeChatId] || []).length === 0;
    const userId = apiService.getStoredUserId();

    // Check if this message is from one of the prompt cards
    const isFromPromptCard = samplePrompts.some(promptCard => promptCard.prompt === prompt);
    const isBenchmarkPrompt = samplePrompts.some(promptCard => 
      promptCard.title === "Benchmarking technologies" && promptCard.prompt === prompt
    );
    console.log(`🎯 Message from prompt card: ${isFromPromptCard}, Benchmark: ${isBenchmarkPrompt}`, { prompt, samplePrompts });

    // Add prefix for benchmark prompts
    let processedPrompt = prompt;
    if (isBenchmarkPrompt) {
      processedPrompt = `I want to benchmark: ${prompt}`;
    }

    setIsLoading(true);
    setIsExtensiveSearchLoading(false); // Regular search

    const attachments: Attachment[] = files.map(file => ({
        name: file.name,
        type: file.type,
        url: URL.createObjectURL(file),
        size: file.size,
    }));

    const userMessage: ChatMessage = { 
        id: Date.now().toString(), 
        text: prompt,  // Display original prompt to user
        sender: Sender.User, 
        attachments 
    };
    setMessages(prev => ({
        ...prev,
        [activeChatId]: [...(prev[activeChatId] || []), userMessage]
    }));

    try {
        // Get the current session ID for this chat (if any)
        // If not found in sessionStorage, use activeChatId itself (which is the chat_session_id)
        let currentSessionId = sessionStorage.getItem(`chat_session_${activeChatId}`);
        if (!currentSessionId && activeChatId) {
            // Use activeChatId as session_id since it's the chat_session_id from database
            currentSessionId = activeChatId;
            sessionStorage.setItem(`chat_session_${activeChatId}`, activeChatId);
        }
        
        // Use V2 endpoint with LanceDB (temporarily hardcoded to true for testing)
        const envVar = process.env.VITE_USE_LANCE_DB;
        const useV2 = true; // Force V2 for testing - change to: envVar !== 'false' later
        
        console.log('🔍 Conversation API Debug:', {
          useV2,
          envVar,
          envVarType: typeof envVar,
          envVarValue: envVar,
          allEnvVars: {
            VITE_API_BASE_URL: process.env.VITE_API_BASE_URL,
            VITE_ENVIRONMENT: process.env.VITE_ENVIRONMENT,
            VITE_USE_LANCE_DB: process.env.VITE_USE_LANCE_DB
          },
          endpoint: useV2 ? '/api/v1/conversation/process/v2' : '/api/v1/conversation/process'
        });
        
        // Track last query and files for extensive search re-run
        // Extensive search toggle for logged in users
        const currentExtensiveSearch = true; // Default to false (non-extensive)
        setLastQuery(processedPrompt);
        setLastFiles(files);
        setLastExtensiveSearch(currentExtensiveSearch);
        
        // Reset streaming state
        setStreamingStatus(null);
        setStreamingMessage(null);
        setStreamingResultsCount(0);
        
        // Call the streaming conversation process API
        const response = await apiService.processConversationV2Stream(
          processedPrompt,  // Use processed prompt with prefix for backend
          currentSessionId,
          userId || undefined,
          files,  // Pass files to V2 endpoint
          {
            // Disable follow-ups for non-benchmark prompt cards, enable for benchmark
            enableFollowups: !isFromPromptCard || isBenchmarkPrompt,
            maxFollowups: (isFromPromptCard && !isBenchmarkPrompt) ? 0 : 2,  // Post-login: 2 followups
            extensiveSearch: currentExtensiveSearch,
            // Internal-only: route flagged emails to the Algolia-backed
            // V2 stream endpoint; everyone else stays on LanceDB.
            useAlgoliaBackend: shouldUseAlgoliaForCosmos(loggedInEmail)
          },
          // Status update callback for streaming
          (update) => {
            setStreamingStatus(update.status);
            setStreamingMessage(update.message);
            setStreamingResultsCount(update.results_count);
            console.log(`🔄 Stream: ${update.status} - ${update.message} (${update.results_count} results)`);
            // When user said "yes" to context break: create new chat, show pending message there, switch to it
            const data = (update as any).data;
            if (data?.type === 'context_break_accepted') {
              const newSessionId = data.new_session_id;
              const pendingInput = data.pending_input;
              if (newSessionId && pendingInput && activeChatId) {
                const oldChatId = activeChatId;
                const newChatId = Date.now().toString();
                setChats(prev => [{ id: newChatId, title: 'New Chat', date: 'Just now' }, ...prev]);
                setMessages(prev => ({
                  ...prev,
                  [oldChatId]: [...(prev[oldChatId] || []), { id: (Date.now() + 1).toString(), text: 'Starting a new chat with your query...', sender: Sender.AI }],
                  [newChatId]: [{ id: Date.now().toString(), text: pendingInput, sender: Sender.User }]
                }));
                sessionStorage.setItem(`chat_session_${newChatId}`, newSessionId);
                streamTargetChatIdRef.current = newChatId;
                setActiveChatId(newChatId);
              }
            }
          }
        );

        if (response.success && response.data) {
            // If we switched to a new chat due to context_break_accepted, add final message to that chat
            const targetChatId = streamTargetChatIdRef.current ?? activeChatId;
            if (streamTargetChatIdRef.current) {
              streamTargetChatIdRef.current = null;
            }

            let aiResponseText = '';
            
            // Handle different response types (API returns 'type' not 'response_type')
            if (response.data.type === 'search_results') {
                // Handle search results
                aiResponseText = response.data.summary || 'Here are the search results...';
                // You could add search results rendering here if needed
            } else if (response.data.type === 'followup') {
                // Handle follow-up response (API returns 'question' for follow-ups)
                aiResponseText = response.data.question || response.data.summary || 'Here\'s a follow-up...';
            } else if (response.data.type === 'out_of_scope' || response.data.type === 'out_of_coverage') {
                // Handle out of scope/coverage queries
                aiResponseText = response.data.response || 
                    ((response.data.type as string) === 'out_of_coverage' 
                        ? "I specialize in US, Indian, German university technology transfer. The university or region you're asking about isn't in my current database. I can help you find similar technologies from MIT, Stanford, Columbia, Cornell, Johns Hopkins, UCLA, Purdue, University of Arizona, University of Minnesota, University of Wisconsin-Madison, Indian Institute of Technology Bombay, Madras, Delhi, Agrinnovate, Arizona State University, Max Planck Innovation, Karlsruhe Institute of Technology (KIT), RWTH Aachen University, Durham University, Newcastle University, University of Pittsburgh, Oxford University instead."
                        : "I'm specialized in US, Indian, German university technology transfer and research partnerships. Could you ask about university innovations, research collaborations, or technology licensing instead?");
            } else if ((response.data as any).type === 'context_break') {
                aiResponseText = (response.data as any).response || "I believe your input or response is unrelated to your previous input. Would you like to start a new chat?";
            } else if ((response.data as any).type === 'context_break_accepted') {
                // Handled in stream callback (new chat created and switched); show short ack in that chat
                aiResponseText = 'Starting a new chat with your query...';
            } else if ((response.data as any).type === 'context_break_rejected') {
                aiResponseText = (response.data as any).response || "No problem — continuing in this chat.";
            } else if ((response.data as any).type === 'broad_query_results') {
                const d = response.data as any;
                const totalCount = d.total_results_count || 0;
                const returnedCount = d.returned_results_count || 0;
                if (totalCount > returnedCount) {
                    aiResponseText = d.summary || `Found ${totalCount} results (showing first ${returnedCount}). Would you like to narrow down these results?`;
                } else {
                    aiResponseText = d.summary || `Found ${totalCount} results. Would you like to narrow down these results?`;
                }
            } else if ((response.data as any).type === 'narrow_declined') {
                aiResponseText = (response.data as any).response || "No problem — the broad results are shown above.";
            } else {
                // Default response
                aiResponseText = response.data.summary || response.data.question || 'I understand your question.';
            }

            // Store session ID for future messages in this chat
            if (response.data.session_id && targetChatId) {
                sessionStorage.setItem(`chat_session_${targetChatId}`, response.data.session_id);
            }

            // Auto-generate and update chat title on first message (API returns 'session_title')
            if (targetChatId && (isFirstMessage || targetChatId !== activeChatId) && response.data.session_title) {
                handleRenameChat(targetChatId, response.data.session_title);
                // Refresh chat list to show the new chat with title in sidebar
                setTimeout(() => refreshChatList(), 1000);
            }

            // Create AI message with search results as attachments if available
            const aiMessageAttachments: any[] = [];
            if (response.data.type === 'search_results' && response.data.results) {
                aiMessageAttachments.push({
                    search_results: response.data.results
                });
            } else if ((response.data as any).type === 'broad_query_results' && (response.data as any).all_results) {
                aiMessageAttachments.push({
                    search_results: (response.data as any).all_results,
                    broad_query_meta: {
                        total_results_count: (response.data as any).total_results_count || 0,
                        returned_results_count: (response.data as any).returned_results_count || 0,
                        is_broad_query: true,
                    }
                });
            }

            const aiMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                text: aiResponseText,
                sender: Sender.AI,
                attachments: aiMessageAttachments.length > 0 ? aiMessageAttachments : undefined,
                dbMessageId: response.data.ai_message_id, // Store database message ID for feedback
                extensiveSearch: response.data.extensive_search ?? false // Store extensive search flag
            };
            // Mark this message as fresh (just created)
            setFreshMessageIds(prev => new Set([...prev, aiMessage.id]));
            setMessages(prev => ({
                ...prev,
                [targetChatId]: [...(prev[targetChatId] || []), aiMessage]
            }));

        } else {
            // Fallback to Gemini if API fails
            console.warn('FastAPI conversation failed, falling back to Gemini');
    let aiResponseText;

    if (isFirstMessage) {
        const [response, title] = await Promise.all([
            generateResponse(prompt, files),
            generateChatTitle(prompt)
        ]);
        aiResponseText = response;
        if (title) {
            handleRenameChat(activeChatId, title);
        }
    } else {
        aiResponseText = await generateResponse(prompt, files);
    }
    
            const aiMessage: ChatMessage = { 
                id: (Date.now() + 1).toString(), 
                text: aiResponseText, 
                sender: Sender.AI 
            };
    setMessages(prev => ({
        ...prev,
        [activeChatId]: [...(prev[activeChatId] || []), aiMessage]
    }));
        }
    } catch (error) {
        console.error('Error in conversation API:', error);
        // Fallback to Gemini on error
        let aiResponseText;

        if (isFirstMessage) {
            const [response, title] = await Promise.all([
                generateResponse(prompt, files),
                generateChatTitle(prompt)
            ]);
            aiResponseText = response;
            if (title) {
                handleRenameChat(activeChatId, title);
            }
        } else {
            aiResponseText = await generateResponse(prompt, files);
        }
        
        const aiMessage: ChatMessage = { 
            id: (Date.now() + 1).toString(), 
            text: aiResponseText, 
            sender: Sender.AI 
        };
        setMessages(prev => ({
            ...prev,
            [activeChatId]: [...(prev[activeChatId] || []), aiMessage]
        }));
    } finally {
    setIsLoading(false);
    setIsExtensiveSearchLoading(false);
    // Clear streaming state
    setStreamingStatus(null);
    setStreamingMessage(null);
    setStreamingResultsCount(0);
    }
  };
  
  const handleRenameChat = (chatId: string, newTitle: string) => {
    setChats(prev => prev.map(chat => chat.id === chatId ? {...chat, title: newTitle} : chat));
  }

  // Handler to re-run search with extensive search enabled
  const handleRunExtensiveSearch = async () => {
    if (!activeChatId || !lastQuery) return;
    
    setIsLoading(true);
    setIsExtensiveSearchLoading(true); // Extensive search
    
    // Reset streaming state
    setStreamingStatus(null);
    setStreamingMessage(null);
    setStreamingResultsCount(0);
    
    try {
      // Get the current session ID for this chat (if any)
      // If not found in sessionStorage, use activeChatId itself (which is the chat_session_id)
      let currentSessionId = sessionStorage.getItem(`chat_session_${activeChatId}`);
      if (!currentSessionId && activeChatId) {
        // Use activeChatId as session_id since it's the chat_session_id from database
        currentSessionId = activeChatId;
        sessionStorage.setItem(`chat_session_${activeChatId}`, activeChatId);
      }
      const userId = apiService.getStoredUserId();
      
      // Call streaming API with extensive search enabled
      const response = await apiService.processConversationV2Stream(
        lastQuery,
        currentSessionId,
        userId || undefined,
        lastFiles,
        {
          enableFollowups: false, // Disable follow-ups for extensive search
          maxFollowups: 0,
          extensiveSearch: true, // Enable extensive search
          // Internal-only Algolia routing — same gate as the regular search.
          useAlgoliaBackend: shouldUseAlgoliaForCosmos(loggedInEmail)
        },
        // Status update callback for streaming
        (update) => {
          setStreamingStatus(update.status);
          setStreamingMessage(update.message);
          setStreamingResultsCount(update.results_count);
          console.log(`🔄 Extensive Stream: ${update.status} - ${update.message} (${update.results_count} results)`);
        }
      );
      
      if (response.success && response.data) {
        let aiResponseText = '';
        
        if (response.data.type === 'search_results') {
          aiResponseText = response.data.summary || 'Here are the extensive search results...';
        } else {
          aiResponseText = response.data.summary || response.data.question || 'I understand your question.';
        }
        
        // Store session ID
        if (response.data.session_id) {
          sessionStorage.setItem(`chat_session_${activeChatId}`, response.data.session_id);
        }
        
        // Create AI message with search results
        const aiMessageAttachments: any[] = [];
        if (response.data.type === 'search_results' && response.data.results) {
          // Get previous non-extensive search results to deduplicate
          const previousMessages = messages[activeChatId] || [];
          const previousNonExtensiveResults: Set<string> = new Set();
          
          // Find the last AI message with non-extensive search results
          for (let i = previousMessages.length - 1; i >= 0; i--) {
            const msg = previousMessages[i];
            if (msg.sender === Sender.AI && 
                msg.attachments && 
                !msg.extensiveSearch && // Only non-extensive searches
                msg.attachments.some((att: any) => att.search_results)) {
              
              // Extract all result IDs from previous non-extensive search
              msg.attachments.forEach((att: any) => {
                if (att.search_results && Array.isArray(att.search_results)) {
                  att.search_results.forEach((result: any) => {
                    // Use utech_technology_id for technologies, utech_researcher_id for researchers, or id as fallback
                    const resultId = result.metadata?.utech_technology_id || 
                                   result.metadata?.utech_researcher_id ||
                                   result.metadata?.id?.toString() || 
                                   result.id?.toString() ||
                                   result.utech_technology_id ||
                                   result.utech_researcher_id ||
                                   result.id;
                    if (resultId) {
                      previousNonExtensiveResults.add(resultId.toString());
                    }
                  });
                }
              });
              break; // Only check the most recent non-extensive search
            }
          }
          
          // Filter out results that were already shown in previous non-extensive search
          const filteredResults = response.data.results.filter((result: any) => {
            // Use utech_technology_id for technologies, utech_researcher_id for researchers, or id as fallback
            const resultId = result.metadata?.utech_technology_id || 
                            result.metadata?.utech_researcher_id ||
                            result.metadata?.id?.toString() || 
                            result.id?.toString() ||
                            result.utech_technology_id ||
                            result.utech_researcher_id ||
                            result.id;
            return !resultId || !previousNonExtensiveResults.has(resultId.toString());
          });
          
          console.log(`🔍 Extensive search: ${response.data.results.length} total results, ${previousNonExtensiveResults.size} duplicates removed, ${filteredResults.length} new results`);
          
          aiMessageAttachments.push({
            search_results: filteredResults
          });
        }
        
        const aiMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: aiResponseText,
          sender: Sender.AI,
          attachments: aiMessageAttachments.length > 0 ? aiMessageAttachments : undefined,
          dbMessageId: response.data.ai_message_id,
          extensiveSearch: true // Mark as extensive search
        };
        
        // Mark this message as fresh (just created)
        setFreshMessageIds(prev => new Set([...prev, aiMessage.id]));
        setMessages(prev => ({
          ...prev,
          [activeChatId]: [...(prev[activeChatId] || []), aiMessage]
        }));
        
        // Update last extensive search flag
        setLastExtensiveSearch(true);
      }
    } catch (error) {
      console.error('Error running extensive search:', error);
    } finally {
      setIsLoading(false);
      setIsExtensiveSearchLoading(false);
      // Clear streaming state
      setStreamingStatus(null);
      setStreamingMessage(null);
      setStreamingResultsCount(0);
    }
  };

  const handleDownloadChat = async (chatId: string) => {
    const chatToDownload = chats.find(c => c.id === chatId);
    const messagesToDownload = messages[chatId];
    if(!chatToDownload || !messagesToDownload) return;

    try {
      // Create a temporary div with the chat content
      const chatContainer = document.createElement('div');
      chatContainer.style.cssText = `
        font-family: 'Space Grotesk', Arial, sans-serif;
        max-width: 800px;
        margin: 0 auto;
        padding: 40px;
        background: white;
        color: #333;
        line-height: 1.6;
      `;

      // Add Cosmos logo and header
      chatContainer.innerHTML = `
        <div style="text-align: center; margin-bottom: 40px; border-bottom: 2px solid #667eea; padding-bottom: 20px;">
          <div style="display: inline-flex; align-items: center; gap: 12px; margin-bottom: 16px;">
            <svg width="32" height="32" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20.5 0C9.17893 0 0 9.17893 0 20.5C0 31.8211 9.17893 41 20.5 41C31.8211 41 41 31.8211 41 20.5C41 9.17893 31.8211 0 20.5 0Z" fill="#667eea"/>
              <path d="M20.5 8C14.701 8 10 12.701 10 18.5C10 24.299 14.701 29 20.5 29C26.299 29 31 24.299 31 18.5C31 12.701 26.299 8 20.5 8Z" fill="white"/>
              <circle cx="20.5" cy="18.5" r="6" fill="#667eea"/>
            </svg>
            <h1 style="font-size: 28px; font-weight: 600; color: #667eea; margin: 0;">Cosmos Chat</h1>
          </div>
          <h2 style="font-size: 20px; font-weight: 500; color: #333; margin: 0;">${chatToDownload.title}</h2>
          <p style="font-size: 14px; color: #666; margin: 8px 0 0 0;">Downloaded on ${new Date().toLocaleDateString()}</p>
        </div>
        <div id="messages-container"></div>
        <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #999;">
          Generated by UniversityTech.io • Connecting Innovation
        </div>
      `;

      const messagesContainer = chatContainer.querySelector('#messages-container');
      
      // Add each message
      messagesToDownload.forEach((message, index) => {
        const messageDiv = document.createElement('div');
        messageDiv.style.cssText = `
          margin-bottom: 24px;
          padding: 16px;
          border-radius: 12px;
          ${message.sender === 'user' 
            ? 'background: #f0f8ff; border-left: 4px solid #667eea; margin-left: 40px;' 
            : 'background: #f8f9fa; border-left: 4px solid #28a745; margin-right: 40px;'
          }
        `;
        
        messageDiv.innerHTML = `
          <div style="font-weight: 600; color: #333; margin-bottom: 8px; font-size: 14px;">
            ${message.sender === 'user' ? '👤 You' : '🤖 Cosmos AI'}
          </div>
          <div style="color: #333; font-size: 14px; white-space: pre-wrap;">
            ${message.text.replace(/\n/g, '<br>')}
          </div>
          ${message.attachments && message.attachments.length > 0 
            ? `<div style="margin-top: 12px; font-size: 12px; color: #666;">
                📎 Attachments: ${message.attachments.map(a => a.name).join(', ')}
               </div>`
            : ''
          }
        `;
        
        messagesContainer?.appendChild(messageDiv);
      });

      // Add the container to the document temporarily
      document.body.appendChild(chatContainer);

      // Use html2canvas to convert to image, then jsPDF to create PDF
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');

      const canvas = await html2canvas(chatContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      // Remove the temporary container
      document.body.removeChild(chatContainer);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      pdf.save(`cosmos-chat-${chatToDownload.title.replace(/\s/g, '_')}-${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Fallback to JSON download if PDF generation fails
      const chatData = {
          chat: chatToDownload,
          messages: messagesToDownload,
      };

      const jsonString = `data:text/json;charset=utf-8,${encodeURIComponent(
        JSON.stringify(chatData, null, 2)
      )}`;
      const link = document.createElement("a");
      link.href = jsonString;
      link.download = `chat-${chatToDownload.title.replace(/\s/g, '_')}-${chatId}.json`;
      link.click();
    }
  }
  
  const activeMessages = activeChatId ? messages[activeChatId] || [] : [];
  
  const openModal = (type: ModalType) => setActiveModal(type);
  const closeModal = () => setActiveModal(null);

  // `reason` is whatever the user typed in the modal's optional "why are you
  // leaving" box; it rides along with the request so the admin archive keeps it.
  const handleDeleteAccount = async (reason?: string): Promise<string | undefined> => {
    const userId = apiService.getStoredUserId();
    if (!userId) {
      console.error("No user ID available");
      closeModal();
      return;
    }

    try {
      const response = await apiService.requestAccountDeletion(userId, reason);
      
      if (response.success) {
        console.log("✅ Account deletion request created successfully");
        // Keep modal open to show success message. The server's date is
        // handed back so the modal shows the same day the email promises
        // (the window is an admin setting, not always 7 days).
        return response.data?.scheduled_deletion_date || undefined;
      } else {
        console.error("❌ Account deletion request failed:", response.error);
        alert(friendlyAuthError(response.error, 'Failed to request account deletion. Please try again.'));
        closeModal();
      }
    } catch (error) {
      console.error("Error requesting account deletion:", error);
      alert("An error occurred while requesting account deletion. Please try again.");
      closeModal();
    }
  };

  const handlePreview = (message: string, summaryType: 'personal' | 'rd',
                        techManagerName: string) => {
    setContactMessage(message);
    setContactSummaryType(summaryType);
    setContactTechManagerName(techManagerName);
    setActiveModal(ModalType.PreviewMessage);
  };

  // Removed handleSendMessageToUniversity - now using direct email sending in PreviewMessageModal

  const handleSendInboxMessage = async (content: string, files: File[] = []) => {
    if (!activeConversationId || !content.trim()) return;

    try {
      const userId = apiService.getStoredUserId();
      if (!userId) return;

      const conversation = inboxConversations.find(c => c.id === activeConversationId);
      if (!conversation) return;

      // Create reply subject using the actual conversation subject
      const originalSubject = conversation.subject || conversation.techCardTitle || 'Technology Inquiry';
      const subject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`;

      const emailPayload = {
        to_email: conversation.participantEmail,
        subject: subject,
        content: content,
        conversation_id: activeConversationId,
        user_id: userId,
        tracking_consent: true,
        is_reply: true
      };

      const response = await apiService.sendTrackedEmail(emailPayload);
      
      if (response.success) {
        // Add message to local state optimistically
    const attachments: Attachment[] = files.map(file => ({
        name: file.name,
        type: file.type,
        url: URL.createObjectURL(file),
        size: file.size,
    }));

    const newMessage: InboxMessageItem = {
          id: response.data?.message_id || Date.now().toString(),
        sender: Sender.User,
        content: content,
        timestamp: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }),
        status: MessageStatus.Delivered,
        attachments,
    };

    setInboxConversations(prev => prev.map(convo => {
        if (convo.id === activeConversationId) {
            return {
                ...convo,
                messages: [...convo.messages, newMessage],
                lastMessageTimestamp: 'Just now',
                messagePreview: content.substring(0, 50) + '...',
            };
        }
        return convo;
    }));

        console.log('Email reply sent successfully:', response.data);
      } else {
        console.error('Failed to send email reply:', response.error);
        // TODO: Show error toast/notification
      }
    } catch (error) {
      console.error('Error sending email reply:', error);
      // TODO: Show error toast/notification
    }
  };

  // Helper function to extract contact name from email content
  const extractContactNameFromContent = (messages: any[]): string => {
    // Look through messages to find "Dear XYZ" pattern
    for (const message of messages) {
      const content = message.content_text || message.content_html || '';
      
      // Look for "Dear [Name]" pattern
      const dearMatch = content.match(/Dear\s+([^,\n]+)/i);
      if (dearMatch && dearMatch[1]) {
        let name = dearMatch[1].trim();
        // Clean up common titles and extra text
        name = name.replace(/\s*(Dr\.?|Prof\.?|Professor|Mr\.?|Ms\.?|Mrs\.?)\s*/gi, '');
        name = name.replace(/[<>]/g, ''); // Remove any HTML artifacts
        if (name && name.length > 1 && name.length < 50) {
          return name;
        }
      }
      
      // Alternative: Look for email signatures or names in content
      const signatureMatch = content.match(/Best regards?,?\s*\n?\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i);
      if (signatureMatch && signatureMatch[1]) {
        return signatureMatch[1].trim();
      }
    }
    
    return ''; // Return empty string if no name found
  };

  const handleSelectConversation = async (conversationId: string) => {
    setActiveConversationId(conversationId);
    setIsConversationLoading(true);
    
    // Load detailed conversation data if needed
    try {
      const response = await apiService.getEmailConversation(conversationId);
      if (response.success && response.data) {
        const detailedConv = response.data;
        
        // Extract contact name from email content
        const extractedContactName = extractContactNameFromContent(detailedConv.messages);
        
        // Update the conversation with detailed data including events
        setInboxConversations(prev => prev.map(conv => {
          if (conv.id === conversationId) {
            // Map detailed messages with status from events
            const detailedMessages = detailedConv.messages.map((msg: any) => ({
              id: msg.id,
              sender: msg.type === 'sent' ? Sender.User : Sender.University,
              content: msg.content_text || msg.content_html || '',
              timestamp: new Date(msg.created_at).toLocaleString(),
              status: getMessageStatus(detailedConv.events, msg.id),
              attachments: []
            }));

            return {
              ...conv,
              messages: detailedMessages,
              status: detailedConv.status,
              contactName: extractedContactName || conv.contactName, // Use extracted name if available
              participantName: extractedContactName || conv.participantName
            };
          }
          return conv;
        }));
      }
    } catch (error) {
      console.error('Failed to load conversation details:', error);
    } finally {
      setIsConversationLoading(false);
    }
  };

  const activeConversation = activeConversationId ? inboxConversations.find(c => c.id === activeConversationId) : null;
  
  // Debug: Log active conversation changes
  useEffect(() => {
    if (activeConversation) {
      console.log('Active conversation updated:', activeConversation);
      console.log('Active conversation messages:', activeConversation.messages);
    }
  }, [activeConversation]);
  const showRightSidebar = activeView === 'cosmos' || activeView === 'inbox';

  /** A gated page not yet open to everyone: the "Super admin preview" /
   *  "Preview" row above it (components/AdminPreview). */
  const withAdminPreview = (feature: FeatureKey, page: React.ReactNode) => (
    access.features[feature].preview ? (
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <AdminPreviewBar feature={feature} />
        <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden">{page}</div>
      </div>
    ) : page
  );
  const headerFeature: FeatureKey | null = activeView === 'foryou' || activeView === 'whatsnew' || activeView === 'ocean'
    ? activeView : null;
  const headerPreview = headerFeature !== null && access.features[headerFeature].preview;

  const renderMainContent = () => {
    switch (activeView) {
      case 'cosmos':
        if (isChatLoading) {
          return <ChatLoadingSkeleton />;
        }
        return <ChatInterface 
                  messages={activeMessages}
                  onSend={handleSendMessage}
                  isLoading={isLoading}
                  isExtensiveSearch={isExtensiveSearchLoading}
                  openModal={openModal}
                  samplePrompts={samplePrompts}
                  userName={`${profileData.firstName} ${profileData.lastName}`.trim()}
                  personalSummary={profileData.summary}
                  rdSummary={rdProfileData.rdSummary}
                  sessionId={sessionStorage.getItem(`chat_session_${activeChatId}`) || activeChatId}
                  profileIncomplete={profileCompletionPercentage < 100}
                  activeChatId={activeChatId}
                  onRunExtensiveSearch={handleRunExtensiveSearch}
                  freshMessageIds={freshMessageIds}
                  streamingStatus={streamingStatus}
                  streamingMessage={streamingMessage}
                  streamingResultsCount={streamingResultsCount}
                />;
      case 'foryou':
        // Feature access "foryou" (utils/featureAccess.ts): the new page where
        // enabled, else the legacy page members had before 24 Oct. Neither
        // mounts before the answer, so neither page's calls go out for nothing.
        if (!access.loaded) return <FeatureAccessWait />;
        if (access.features.foryou.enabled) {
          return withAdminPreview('foryou',
            <ForYouPage interestsData={interestsData} setActiveView={setActiveView} onReadMore={handleReadMore} onNavigateToProfileTab={handleNavigateToProfileTab} />);
        }
        return <ForYouPageLegacy interestsData={interestsData} setActiveView={setActiveView} onReadMore={handleReadMore} onNavigateToProfileTab={handleNavigateToProfileTab} />;
      case 'whatsnew':
        // Feature access "whatsnew"; the effect above leaves the view when it is not enabled.
        if (!access.features.whatsnew.enabled) return <FeatureAccessWait />;
        // "View new tech offers →" lists them inside What's New (a tech opens the Cosmos card); the list's
        // footer, and "Explore" on a new university, open Cosmos filtered to it (utils/cosmosSeed). Explorers
        // get the plans sheet.
        return withAdminPreview('whatsnew', <WhatsNewPage onOpenTech={id => handleReadMore(SavedCardType.Tech, id)}
          onOpenCosmos={code => {
            setCosmosSeed({ university: code, source: 'whatsnew' });
            setActiveView('directsearch');
          }} />);
      case 'ocean':
        // Feature access "ocean": OCEAN (components/ocean) where enabled,
        // else the Coming Soon page.
        if (!access.loaded) return <FeatureAccessWait />;
        if (access.features.ocean.enabled) {
          return withAdminPreview('ocean', (
            <OceanApp onOpenProfile={tab => handleNavigateToProfileTab(tab || 'ocean')}
                      onOpenTech={(id, relevance) => handleReadMore(SavedCardType.Tech, id, relevance)} />
          ));
        }
        // The upgrade CTA goes through the same paywall as every other locked
        // feature, so the plan copy and the checkout stay in one place; paid
        // accounts get an acknowledgement instead.
        return <OceanPage
                 isPaid={!!upgrade.ent.paid}
                 onUpgrade={() => upgrade.gate(false, 'Ocean early access')} />;
      case 'challenges':
        return <ChallengesPage />;
      // ---- FE-B: in-platform IP Intel tab (SideNav "IP Intel") ----
      // The public /ip-intel-report page without its site chrome; events and
      // orders carry {surface: 'platform', entry: 'tab'}, and a paid order is
      // confirmed in place rather than leaving the app.
      case 'ipintel':
        return <IpIntelReportPage embedded />;
      // ---- end FE-B ----
      case 'directsearch':
        // Allowlist-gated. If a non-internal user lands here (e.g. cached
        // activeView in sessionStorage), fall back to the default chat view
        // rather than rendering the page they shouldn't see.
        if (!canSeeDirectSearch(loggedInEmail)) {
          return <ChatInterface
                    messages={activeMessages}
                    onSend={handleSendMessage}
                    isLoading={isLoading}
                    isExtensiveSearch={isExtensiveSearchLoading}
                    openModal={openModal}
                    samplePrompts={samplePrompts}
                    userName={`${profileData.firstName} ${profileData.lastName}`.trim()}
                    personalSummary={profileData.summary}
                    rdSummary={rdProfileData.rdSummary}
                    sessionId={sessionStorage.getItem(`chat_session_${activeChatId}`) || activeChatId}
                    profileIncomplete={profileCompletionPercentage < 100}
                    activeChatId={activeChatId}
                    onRunExtensiveSearch={handleRunExtensiveSearch}
                    freshMessageIds={freshMessageIds}
                    streamingStatus={streamingStatus}
                    streamingMessage={streamingMessage}
                    streamingResultsCount={streamingResultsCount}
                  />;
        }
        return <DirectSearchPage
          onReadMore={(id, relevance) => handleReadMore(SavedCardType.Tech, id, relevance)}
          openModal={openModal}
          onContact={(card, surface) => contactFromCard(card, surface === 'cosmos_picks' ? 'cosmos_picks' : 'search_results')}
          // Onboarding v2: an Explorer's 3 picks + the upgrade block in place
          // of Popular (the page checks the account is a real Explorer).
          explorerLanding={{
            enabled: onboardingFlow === 'v2' && !!onboardingConfig?.explorer_picks,
            onboardingOpen: onboardingVisible,
            choice: explorerChoice,
            copy: onboardingConfig?.copy,
            pending: !onboardingConfigResolved,
            // Admin → Onboarding → "Lock search for Explorers" (null: not
            // known, so the page falls back to the account's can_search).
            searchLocked: typeof onboardingConfig?.search_locked === 'boolean'
              ? onboardingConfig.search_locked : null,
          }}
          onStartTrial={origin => { void startTrialInPlace(undefined, origin); }}
          trial={upgradeTrial}
        />;
      case 'inbox':
        if (isInboxLoading) {
          return <InboxLoadingSkeleton />;
        }
        if (inboxConversations.length === 0) {
            return (
                <main className="flex flex-1 flex-col self-stretch rounded-2xl bg-white shadow-custom-light p-4 sm:p-12 justify-center items-center">
                    <EmptyInbox onGoToCosmos={() => setActiveView('cosmos')} />
                </main>
            );
        }
        if (isConversationLoading) {
          return <InboxLoadingSkeleton />;
        }
        return activeConversation ? 
            <InboxPage 
              conversation={activeConversation} 
              onReadMore={() => handleReadMore(SavedCardType.Tech, activeConversation.techCardId)}
              onSendMessage={handleSendInboxMessage}
            /> : 
            <main className="flex flex-1 flex-col self-stretch rounded-2xl bg-white shadow-custom-light p-4 sm:p-12 justify-center items-center">
                <p>Please select a conversation to view.</p>
            </main>;
      default:
        if (isChatLoading) {
          return <ChatLoadingSkeleton />;
        }
        return <ChatInterface 
                  messages={activeMessages}
                  onSend={handleSendMessage}
                  isLoading={isLoading}
                  isExtensiveSearch={isExtensiveSearchLoading}
                  openModal={openModal}
                  samplePrompts={samplePrompts}
                  userName={`${profileData.firstName} ${profileData.lastName}`.trim()}
                  personalSummary={profileData.summary}
                  rdSummary={rdProfileData.rdSummary}
                  sessionId={sessionStorage.getItem(`chat_session_${activeChatId}`) || activeChatId}
                  profileIncomplete={profileCompletionPercentage < 100}
                  activeChatId={activeChatId}
                  onRunExtensiveSearch={handleRunExtensiveSearch}
                  freshMessageIds={freshMessageIds}
                  streamingStatus={streamingStatus}
                  streamingMessage={streamingMessage}
                  streamingResultsCount={streamingResultsCount}
                />;
    }
  };

  // Show loading screen during initial data loading
  if (isInitialLoading) {
    return <LoadingScreen message="Loading your workspace..." showLogo={true} />;
  }

  return (
    <div className="h-full w-full flex flex-col lg:flex-row lg:p-4 lg:gap-3 bg-gradient-to-b from-[rgba(249,247,243,0.9)] to-[rgba(249,247,243,0.9)] bg-white font-grotesk text-grayscale-1300">
        <header className="lg:hidden flex justify-between items-center px-4 h-14 bg-white/80 backdrop-blur-sm border-b border-primary-200/50 z-20 flex-shrink-0">
            <button onClick={() => { trackClick('nav_click_hamburger'); setIsLeftSidebarOpen(true); }} className="p-2 -ml-2"><HamburgerMenuIcon /></button>
            <span className="flex items-center gap-1.5 min-w-0 px-2">
              <span className="font-bold text-grayscale-1300 text-lg truncate">{headerTitle}</span>
              {headerPreview && headerFeature && <AdminPreviewBadge mode={access.features[headerFeature].mode} />}
            </span>
            <div className="flex items-center gap-1">
              <UpgradeNavButton compact />
              <button onClick={() => setIsRightSidebarOpen(true)} className={`p-2 -mr-2 ${!showRightSidebar ? 'invisible' : ''}`}>
                  <ClipboardTextIcon />
              </button>
            </div>
        </header>

        {/* Mobile Left Sidebar */}
        <div className={`lg:hidden fixed inset-0 z-40 transition-opacity ${isLeftSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={closeSidebars}>
            <div className="absolute inset-0 bg-black/30"></div>
            <aside className={`relative h-full w-20 bg-white transition-transform transform ${isLeftSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} onClick={e => e.stopPropagation()}>
                <SideNav 
                    activeView={activeView} 
                    setActiveView={handleViewChange} 
                    userName={profileData.firstName}
                    userFirstName={profileData.firstName}
                    userLastName={profileData.lastName}
                    userEmail={profileData.email}
                    userProfileImage={profileData.profileImage}
                    profileCompletionPercentage={profileCompletionPercentage}
                    features={access.features}
                />
            </aside>
        </div>

        {/* Desktop Left Sidebar */}
        <aside className="hidden lg:flex">
            <SideNav 
                activeView={activeView} 
                setActiveView={handleViewChange} 
                userName={profileData.firstName}
                userFirstName={profileData.firstName}
                userLastName={profileData.lastName}
                userEmail={profileData.email}
                userProfileImage={profileData.profileImage}
                profileCompletionPercentage={profileCompletionPercentage}
                features={access.features}
            />
        </aside>
        
        <div className="flex-1 flex min-w-0 overflow-hidden">
             {activeView === 'profile' ? (
                <ProfilePage 
                    profileData={profileData} 
                    setProfileData={setProfileData} 
                    initialProfileData={getInitialProfileData(loggedInEmail || '')}
                    rdProfileData={rdProfileData}
                    setRDProfileData={setRDProfileData}
                    initialRDProfileData={initialRDProfileData}
                    interestsData={interestsData}
                    setInterestsData={setInterestsData}
                    initialInterestsData={initialInterestsData}
                    openModal={openModal}
                    onReadMore={handleReadMore}
                    onLogout={onLogout}
                    refreshProfileData={refreshProfileData}
                    setActiveView={setActiveView}
                    initialTab={profileInitialTab}
                    onTabChange={setProfileTab}
                    userHasPassword={userHasPassword}
                />
            ) : activeView === 'privacy-policy' ? (
                <PrivacyPolicyPage onClose={() => setActiveView('profile')} />
            ) : activeView === 'terms-of-service' ? (
                <TermsOfServicePage onClose={() => setActiveView('profile')} />
            ) : activeView === 'about-us' ? (
                <AboutUsPage 
                    onBack={() => setActiveView('profile')} 
                    onShowPrivacyPolicy={() => setActiveView('privacy-policy')}
                    onShowTermsOfService={() => setActiveView('terms-of-service')}
                />
            ) : (
                <>
                    {renderMainContent()}
                    <aside className={`hidden ${!showRightSidebar ? 'lg:hidden' : 'lg:flex'}`}>
                        <RightSidebar 
                            chats={chats}
                            activeChatId={activeChatId}
                            onSelectChat={handleSelectChat}
                            onAddNewChat={handleAddNewChat}
                            onRenameChat={handleRenameChat}
                            onDownloadChat={handleDownloadChat}
                            openModal={openModal}
                            inboxConversations={inboxConversations}
                            activeConversationId={activeConversationId}
                            onSelectConversation={handleSelectConversation}
                            activeView={activeView}
                            isLoading={isLoading}
                        />
                    </aside>
                </>
            )}
        </div>

        {/* Mobile Right Sidebar */}
        <div className={`lg:hidden fixed inset-0 z-40 transition-opacity ${isRightSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={closeSidebars}>
            <div className="absolute inset-0 bg-black/30"></div>
            <aside className={`absolute top-0 right-0 h-full w-[276px] bg-white transition-transform transform ${isRightSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`} onClick={e => e.stopPropagation()}>
                <RightSidebar 
                    onClose={closeSidebars}
                    chats={chats}
                    activeChatId={activeChatId}
                    onSelectChat={(id) => {handleSelectChat(id); handleViewChange('cosmos')}}
                    onAddNewChat={handleAddNewChat}
                    onRenameChat={handleRenameChat}
                    onDownloadChat={handleDownloadChat}
                    openModal={openModal}
                    inboxConversations={inboxConversations}
                    activeConversationId={activeConversationId}
                    onSelectConversation={(id) => {handleSelectConversation(id); handleViewChange('inbox')}}
                    activeView={activeView}
                    isLoading={isLoading}
                />
            </aside>
        </div>

        {techCardModalOpen && (
            <div 
                className="fixed inset-0 w-full h-full flex justify-center items-center bg-black/70 backdrop-blur-sm z-50 p-4"
                onClick={closeTechCardModal}
            >
                <div className="utech-nocopy" onClick={e => e.stopPropagation()}>
                    {cardDataLoading ? (
                        <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
                            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-grayscale-600 font-grotesk">Loading technology details...</p>
                        </div>
                    ) : selectedCardData && techCardDesign === 'accordion' && accordionFailedId !== selectedCardId ? (
                    <TechAccordionCard
                        variant="modal"
                        techId={String(selectedCardData?.utech_technology_id || selectedCardData?.id || selectedCardId || '')}
                        preview={{ title: selectedCardData?.title, university: selectedCardData?.institution, country: selectedCardData?.location, trl: selectedCardData?.trl }}
                        relevance={activeView === 'inbox' || activeView === 'foryou' ? null : cardRelevance}
                        surface={`modal_${activeView}`}
                        onClose={closeTechCardModal}
                        contactLabel={activeView === 'inbox' ? 'Already Contacted' : undefined}
                        contactDisabled={activeView === 'inbox'}
                        onContact={card => { if (activeView !== 'inbox') contactFromCard(card, 'tech_modal'); }}
                        bookmarked={inboxSavedStates.get(selectedCardData?.utech_technology_id || selectedCardData?.id)?.bookmarked}
                        onBookmarkChange={(assetId, on) => {
                            setInboxSavedStates(prev => {
                                const next = new Map(prev);
                                const key = selectedCardData?.utech_technology_id || selectedCardData?.id || assetId;
                                next.set(key, { ...(next.get(key) || { bookmarked: false, liked: null }), bookmarked: on });
                                return next;
                            });
                            // The Saved tab (Profile) lists bookmarks; it reloads on this.
                            window.dispatchEvent(new Event('utech:saved-changed'));
                        }}
                        onOpenTech={id => {
                            const cur = String(selectedCardData?.utech_technology_id || selectedCardData?.id || selectedCardId || '');
                            if (cur && cur !== id) {
                                setTechModalTrail(t => [...t, { id: cur, title: String(selectedCardData?.title || '') }].slice(-20));
                            }
                            handleReadMore(SavedCardType.Tech, id);
                        }}
                        onBack={techModalTrail.length ? () => {
                            const prev = techModalTrail[techModalTrail.length - 1];
                            setTechModalTrail(t => t.slice(0, -1));
                            handleReadMore(SavedCardType.Tech, prev.id);
                        } : undefined}
                        backLabel={techModalTrail.length ? techModalTrail[techModalTrail.length - 1].title : undefined}
                        onLoadError={() => setAccordionFailedId(selectedCardId)}
                    />
                    ) : selectedCardData ? (
                    <TechCard 
                        onClose={closeTechCardModal}
                            {...selectedCardData}
                            contactButtonText={activeView === 'inbox' ? 'Already Contacted' : 'Contact university'}
                            contactDisabled={activeView === 'inbox'}
                            contactLocked={activeView !== 'inbox' && !upgrade.ent.can_contact}
                            onLockedContact={() => upgrade.gate(false, 'university contact details', undefined, 'can_contact')}
                        onContact={() => {
                                // Show "Already Contacted" text for inbox cards
                                if (activeView === 'inbox') {
                                    // Do nothing - already contacted
                                } else {
                            // Keep the tech card open behind the modal: the
                            // addresses are meaningless without the technology
                            // they belong to, and closing it lost that context.
                            setContactSurface('tech_modal');
                            openModal(USE_TTO_CONTACT_MODAL
                                ? ModalType.ContactTTO : ModalType.ContactUniversity);
                                }
                            }}
                            onBookmarkToggle={async (isBookmarked) => {
                                try {
                                    const userId = apiService.getStoredUserId();
                                    const assetId = selectedCardData?.utech_technology_id || selectedCardData?.id;
                                    if (userId && assetId) {
                                        const response = await apiService.saveCard({
                                            asset_type: 'technology',
                                            asset_id: assetId,
                                            bookmarked: isBookmarked,
                                            liked: null,
                                            linked_chat_id: null
                                        }, userId);
                                        
                                        if (response.success) {
                                            // Update local state
                                            setInboxSavedStates(prev => {
                                                const newMap = new Map(prev);
                                                const current = newMap.get(assetId) || {bookmarked: false, liked: null};
                                                newMap.set(assetId, {...current, bookmarked: isBookmarked});
                                                return newMap;
                                            });
                                            console.log('Bookmark status updated:', isBookmarked);
                                        }
                                    }
                                } catch (error) {
                                    console.error('Error toggling bookmark:', error);
                                }
                            }}
                            onLikeDislike={async (liked) => {
                                try {
                                    const userId = apiService.getStoredUserId();
                                    const assetId = selectedCardData?.utech_technology_id || selectedCardData?.id;
                                    if (userId && assetId) {
                                        const response = await apiService.likeCard({
                                            asset_type: 'technology',
                                            asset_id: assetId,
                                            liked: liked,
                                            linked_chat_id: null
                                        }, userId);
                                        
                                        if (response.success) {
                                            // Update local state
                                            setInboxSavedStates(prev => {
                                                const newMap = new Map(prev);
                                                const current = newMap.get(assetId) || {bookmarked: false, liked: null};
                                                newMap.set(assetId, {...current, liked: liked});
                                                return newMap;
                                            });
                                            console.log('Like status updated:', liked);
                                        }
                                    }
                                } catch (error) {
                                    console.error('Error toggling like:', error);
                                }
                            }}
                            initialBookmarked={(() => {
                                const assetId = selectedCardData?.utech_technology_id || selectedCardData?.id;
                                return inboxSavedStates.get(assetId)?.bookmarked ?? false;
                            })()}
                            initialLiked={(() => {
                                const assetId = selectedCardData?.utech_technology_id || selectedCardData?.id;
                                return inboxSavedStates.get(assetId)?.liked ?? null;
                            })()}
                        />
                    ) : (
                        <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
                            <p className="text-grayscale-600 font-grotesk">Failed to load technology details</p>
                            <button 
                                onClick={closeTechCardModal}
                                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>
            </div>
        )}

        {researcherCardModalOpen && (
            <div 
                className="fixed inset-0 w-full h-full flex justify-center items-center bg-black/70 backdrop-blur-sm z-50 p-4"
                onClick={closeResearcherCardModal}
            >
                <div onClick={e => e.stopPropagation()}>
                    {cardDataLoading ? (
                        <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
                            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-grayscale-600 font-grotesk">Loading researcher details...</p>
                        </div>
                    ) : selectedCardData ? (
                    <ResearcherCard 
                        onClose={closeResearcherCardModal}
                            {...selectedCardData}
                        onTechCardClick={(techId) => {
                            closeResearcherCardModal();
                            handleReadMore(SavedCardType.Tech, techId);
                        }}
                            onBookmarkToggle={async (isBookmarked) => {
                                try {
                                    const userId = apiService.getStoredUserId();
                                    const assetId = selectedCardData?.utech_researcher_id || selectedCardData?.id;
                                    if (userId && assetId) {
                                        const response = await apiService.saveCard({
                                            asset_type: 'researcher',
                                            asset_id: assetId,
                                            bookmarked: isBookmarked,
                                            liked: null,
                                            linked_chat_id: null
                                        }, userId);
                                        
                                        if (response.success) {
                                            // Update local state
                                            setInboxSavedStates(prev => {
                                                const newMap = new Map(prev);
                                                const current = newMap.get(assetId) || {bookmarked: false, liked: null};
                                                newMap.set(assetId, {...current, bookmarked: isBookmarked});
                                                return newMap;
                                            });
                                            console.log('Bookmark status updated:', isBookmarked);
                                        }
                                    }
                                } catch (error) {
                                    console.error('Error toggling bookmark:', error);
                                }
                            }}
                            onLikeDislike={async (liked) => {
                                try {
                                    const userId = apiService.getStoredUserId();
                                    const assetId = selectedCardData?.utech_researcher_id || selectedCardData?.id;
                                    if (userId && assetId) {
                                        const response = await apiService.likeCard({
                                            asset_type: 'researcher',
                                            asset_id: assetId,
                                            liked: liked,
                                            linked_chat_id: null
                                        }, userId);
                                        
                                        if (response.success) {
                                            // Update local state
                                            setInboxSavedStates(prev => {
                                                const newMap = new Map(prev);
                                                const current = newMap.get(assetId) || {bookmarked: false, liked: null};
                                                newMap.set(assetId, {...current, liked: liked});
                                                return newMap;
                                            });
                                            console.log('Like status updated:', liked);
                                        }
                                    }
                                } catch (error) {
                                    console.error('Error toggling like:', error);
                                }
                            }}
                            initialBookmarked={(() => {
                                const assetId = selectedCardData?.utech_researcher_id || selectedCardData?.id;
                                return inboxSavedStates.get(assetId)?.bookmarked ?? false;
                            })()}
                            initialLiked={(() => {
                                const assetId = selectedCardData?.utech_researcher_id || selectedCardData?.id;
                                return inboxSavedStates.get(assetId)?.liked ?? null;
                            })()}
                        />
                    ) : (
                        <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
                            <p className="text-grayscale-600 font-grotesk">Failed to load researcher details</p>
                            <button 
                                onClick={closeResearcherCardModal}
                                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>
            </div>
        )}


        {/* Logout Confirmation Popup */}
        {showLogoutConfirmation && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[70]">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 mx-4">
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Logout?</h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to logout from your account?
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={handleLogoutCancel}
                                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleLogoutConfirm}
                                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-full hover:bg-red-600 transition-colors"
                            >
                                Yes, Logout
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        
        {/* Inactivity Timeout Popup */}
        {showInactivityPopup && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[80]">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 mx-4 transform transition-all">
                    <div className="text-center">
                        <div className="w-20 h-20 mx-auto mb-5 bg-amber-50 rounded-full flex items-center justify-center">
                            <svg className="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Session Timeout Warning</h3>
                        <p className="text-gray-600 mb-4">
                            You've been inactive for a while. For your security, you'll be signed out automatically.
                        </p>
                        <div className="bg-amber-50 rounded-lg py-3 px-4 mb-6">
                            <p className="text-amber-700 font-medium">
                                Time remaining: <span className="text-amber-900 font-bold">
                                    {remainingSeconds >= 3600
                                        ? `${Math.floor(remainingSeconds / 3600)}h ${Math.floor((remainingSeconds % 3600) / 60)}m`
                                        : remainingSeconds >= 300
                                            ? `${Math.ceil(remainingSeconds / 60)} min`
                                            : `${Math.floor(remainingSeconds / 60)}:${(remainingSeconds % 60).toString().padStart(2, '0')}`}
                                </span>
                            </p>
                        </div>
                        <p className="text-sm text-gray-500 mb-6">
                            Would you like to stay logged in?
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={handleInactivityLogout}
                                className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                            >
                                Logout
                            </button>
                            <button
                                onClick={handleStayLoggedIn}
                                className="flex-1 px-4 py-3 text-sm font-medium text-white bg-primary-600 rounded-full hover:bg-primary-700 transition-colors"
                            >
                                Stay Logged In
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        <InviteModal isOpen={activeModal === ModalType.Invite} onClose={closeModal} />
        <FeedbackModal isOpen={activeModal === ModalType.Feedback} onClose={closeModal} />
        <BugReportModal isOpen={activeModal === ModalType.Bug} onClose={closeModal} />
        <BookCallModal isOpen={activeModal === ModalType.BookCall} onClose={closeModal} />
        {USE_TTO_CONTACT_MODAL && selectedCardData && (
          <ContactTTOModal
            isOpen={activeModal === ModalType.ContactTTO}
            onClose={closeModal}
            techId={selectedCardData?.utech_technology_id || selectedCardData?.id || ''}
            techTitle={selectedCardData?.title || ''}
            university={selectedCardData?.institution || selectedCardData?.university_name || ''}
            userName={[profileData.firstName, profileData.lastName].filter(Boolean).join(' ')}
            userEmail={profileData.email || loggedInEmail || ''}
            surface={contactSurface || undefined}
          />
        )}

        {selectedCardData && (
          <ConciergeRequestModal
            isOpen={activeModal === ModalType.ConciergeRequest}
            onClose={closeModal}
            techId={selectedCardData?.utech_technology_id || selectedCardData?.id || ''}
            techTitle={selectedCardData?.title || ''}
            university={selectedCardData?.institution || selectedCardData?.university_name || ''}
            defaultName={`${profileData.firstName} ${profileData.lastName}`.trim()}
            defaultEmail={loggedInEmail || ''}
          />
        )}

        <ContactUniversityModal 
            onTalkToConcierge={() => setActiveModal(ModalType.BookCall)}
            surface={contactSurface || undefined}
            university={selectedCardData?.institution || selectedCardData?.university_name || ''}
            userEmail={profileData.email || loggedInEmail || ''}
            isOpen={activeModal === ModalType.ContactUniversity}
            onClose={closeModal}
            onPreview={handlePreview}
            techTitle={selectedCardData?.title || techCardData.title}
            techId={selectedCardData?.utech_technology_id || selectedCardData?.id || techCardData.id} // Always use utech_technology_id for data fetching
            referenceNumber={selectedCardData?.reference_number || ''} // Separate reference number for display
            researcherName={selectedCardData?.researchers?.[0]?.name || techCardData.researchers[0]?.name || 'Researcher'}
            userName={`${profileData.firstName} ${profileData.lastName}`}
            personalSummary={profileData.summary}
            rdSummary={rdProfileData.rdSummary}
        />
        <PreviewMessageModal
            isOpen={activeModal === ModalType.PreviewMessage}
            onClose={closeModal}
            onEdit={() => setActiveModal(ModalType.ContactUniversity)}
            onSend={async () => {
              // ---------------------------------------------------------------
              // P1 bug fix (2026-05-13 incident): this handler used to read the
              // tech title, salutation and id from the module-level `techCardData`
              // MOCK constant, and resolve `to_email` from `selectedCardData.
              // tech_manager_*` — which is never set by any setSelectedCardData
              // call site. Both defects fired together and produced an email
              // addressed "Dear Robert A. Martin" sent to a dev fallback email.
              //
              // The fix mirrors the working logic in `ChatSearchResults.tsx`
              // and `SavedPage.tsx`:
              //   1. Use `selectedCardData` (real, freshly-fetched tech) for
              //      title / id / first researcher name. Mock is ignored.
              //   2. Call `getTechnologyDetails()` at send-time to pull the
              //      authoritative `tech_manager_details` / `tech_manager_default`
              //      / `common_email` directly from the backend — even if
              //      `selectedCardData` was populated elsewhere without those
              //      fields, the fresh fetch always has them.
              //   3. If no real TTO email can be resolved, abort the send and
              //      show the user an error instead of silently falling back
              //      to a dev address.
              // See `Backend/DOCS/BUG_2026-05-13_misrouted_tto_email.md`.
              // ---------------------------------------------------------------

              const userId = apiService.getStoredUserId();
              if (!userId) return;

              // 1. Authoritative tech metadata from `selectedCardData`. Each
              //    field falls back to the mock only as a last-ditch safety net
              //    (production data will always populate these).
              //
              // `techIdForSend` is the internal `UTECH-…` id used for
              //    backend API calls (e.g. `getTechnologyDetails`).
              // `subjectIdForSend` is the human-readable reference number
              //    (e.g. `T-676`, `UA25-211`) that the subject line + the
              //    conversation_id slug use — this matches the original
              //    pre-bug behaviour where the subject said "Interest in
              //    tech T-676" not "Interest in tech UTECH-CSIR-001304".
              const techIdForSend: string =
                selectedCardData?.utech_technology_id ||
                selectedCardData?.id ||
                techCardData.id;
              const subjectIdForSend: string =
                selectedCardData?.reference_number ||
                techIdForSend;
              const techTitleForSend: string =
                selectedCardData?.title ||
                techCardData.title;
              const researcherNameForSend: string =
                selectedCardData?.researchers?.[0]?.name ||
                'Researcher';

              const summary = contactSummaryType === 'personal' ? profileData.summary : rdProfileData.rdSummary;
              const fullMessageContent = `I found your tech, titled ${techTitleForSend}, on UniversityTech.io and would like to find out more. Here's how we could potentially collaborate, work with your researchers or commercialise your tech: ${contactMessage}\n\nHere's a short profile about us:\n\n${summary}\n\nLook forward to hearing back from you\n${profileData.firstName} ${profileData.lastName}`;
              const conversationId = `${subjectIdForSend}-${Date.now()}-${userId}`;

              const htmlContent = `
                <html>
                <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
                  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
                    <h2 style="color: #2c3e50; margin-bottom: 20px;">Technology Partnership Opportunity</h2>
                    <p style="margin-bottom: 15px;">Dear ${researcherNameForSend},</p>
                    <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
                      ${fullMessageContent.replace(/\n\n/g, '</p><p style="margin-bottom: 15px;">').replace(/\n/g, '<br>')}
                    </div>
                    <p style="margin-top: 20px; font-size: 14px; color: #666;">
                      This email was sent through <a href="https://universitytech.io" style="color: #007bff; text-decoration: none;">UniversityTech.io</a>
                      to facilitate technology transfer and collaboration opportunities.
                    </p>
                    <hr style="border: none; border-top: 1px solid #eee; margin: 20px 0;">
                    <p style="font-size: 12px; color: #999; text-align: center;">
                      This message was sent via UniversityTech.io's secure email system.
                    </p>
                  </div>
                </body>
                </html>
              `;

              // 2. Resolve the recipient by FRESHLY fetching tech details from
              //    the backend. This matches `ChatSearchResults.tsx:568-635`
              //    and `SavedPage.tsx:386-452` step for step — the three send
              //    paths must resolve the same recipient for the same
              //    technology, or a lead is routed differently depending on
              //    which page it was contacted from.
              let techManagerEmails: string[] = [];
              let techManagerNames: string[] = [];
              let commonEmail: string | undefined = undefined;
              let ccEmail: string | undefined = undefined;
              // Set when the account is over its university-contact limit:
              // the contacts endpoint withholds the addresses, so there is no
              // one to send to (and routing it to ops would be wrong too).
              let contactBlockReason: string | null = null;
              try {
                // The metered contacts endpoint: the same four fields the
                // details endpoint used to carry, under `contacts`.
                const contactsResponse = await apiService.getTechnologyContacts(techIdForSend,
                  { surface: 'send_email', purpose: 'send' });
                if (contactsResponse.success && contactsResponse.data && !contactsResponse.data.allowed) {
                  contactBlockReason = contactsResponse.data.reason || 'limit_reached';
                }
                const techContacts: any = contactsResponse.success && contactsResponse.data?.allowed
                  ? contactsResponse.data.contacts : null;
                if (techContacts) {
                  commonEmail = techContacts.common_email || undefined;

                  // Curated tech-transfer office (technologies.tto_office_details), loaded
                  // from the reviewed Global Universities sheet. NULL means not curated yet.
                  // The details endpoint may hand JSON columns back as strings, so parse it
                  // the same way tech_manager_default is treated elsewhere: object, JSON
                  // string, or nothing. Its emails are always an array of clean addresses.
                  const rawOffice = techContacts.tto_office_details;
                  let ttoOffice: any = null;
                  try {
                    ttoOffice = typeof rawOffice === 'object' && rawOffice !== null
                      ? rawOffice
                      : (typeof rawOffice === 'string' && rawOffice.trim() ? JSON.parse(rawOffice) : null);
                  } catch (e) {
                    ttoOffice = null;
                  }
                  const ttoOfficeEmails: string[] = Array.isArray(ttoOffice?.emails)
                    ? ttoOffice.emails.filter((e: any) => typeof e === 'string' && e.trim())
                    : [];

                  // CC: the curated office when we have one (every address; the backend
                  // splits cc_email on ','/';' and drops whichever one becomes TO), else
                  // the legacy common_email string as before.
                  ccEmail = ttoOfficeEmails.length > 0 ? ttoOfficeEmails.join(', ') : commonEmail;

                  // Tier 1: tech_manager_details (list or dict)
                  const tmDetails = techContacts.tech_manager_details;
                  if (tmDetails) {
                    const list = Array.isArray(tmDetails) ? tmDetails : [tmDetails];
                    list.forEach((m: any) => {
                      if (m?.email) {
                        techManagerEmails.push(m.email);
                        if (m.name) techManagerNames.push(m.name);
                      }
                    });
                  }

                  // Tier 2: curated office first, then tech_manager_default.
                  // The curated office is the reviewed contact; tech_manager_default is
                  // scraped, and for 7 universities its "name" is the university's own
                  // name rather than an office (WARF's is a person), so it only comes
                  // into play when nothing curated exists.
                  if (techManagerEmails.length === 0 && ttoOfficeEmails.length > 0) {
                    techManagerEmails.push(ttoOfficeEmails[0]);
                    if (ttoOffice?.name) techManagerNames.push(ttoOffice.name);
                  }
                  if (techManagerEmails.length === 0) {
                    const tmDefault = techContacts.tech_manager_default;
                    if (tmDefault?.email) {
                      techManagerEmails.push(tmDefault.email);
                      if (tmDefault.name) techManagerNames.push(tmDefault.name);
                    }
                  }
                }
              } catch (error) {
                console.warn('Could not fetch tech manager info, will fall through to abort/safe-default:', error);
              }

              if (contactBlockReason) {
                alert(contactBlockReason === 'upgrade_required'
                  ? "University contacts are part of our trial and paid plans, so this message wasn't sent."
                  : "You've reached your limit for university contacts, so this message wasn't sent.\n\n" +
                    'You can request more from the Contact University window.');
                return;
              }

              // Tier 3: SAFE fallback. We used to ship to a hardcoded dev email
              // here. That misrouted real leads (see the bug doc). Now: route
              // to ops if nothing else resolved, and never to a personal
              // mailbox.
              //
              // These values are the RAW field contents and may each hold several
              // ';'/','-separated addresses. The backend parses them and picks the
              // final TO/CC (see api.send_tracked_email) -- don't pre-resolve here,
              // or the three send paths will drift apart again.
              const universityEmail: string =
                techManagerEmails[0] ||
                commonEmail ||
                'info@universitytech.io';

              console.log('📧 Sending email to university:', universityEmail);
              console.log('   Tech ID:', techIdForSend);
              console.log('   Tech Title:', techTitleForSend);
              console.log('   User ID:', userId);
              console.log('   🏛️ Resolved Tech Manager Name(s):', techManagerNames.join(', ') || '(none)');
              console.log('   📧 All Tech Manager Email(s):', techManagerEmails.join(', ') || '(none)');
              console.log('   📧 CC:', ccEmail || '(none)');

              const emailPayload = {
                to_email: universityEmail,
                subject: `Interest in tech ${subjectIdForSend}`,
                content: htmlContent,
                conversation_id: conversationId,
                user_id: userId,
                tracking_consent: true,
                is_reply: false,
                // Pass the internal UTECH-… id so the backend can persist it
                // on email_conversations.utech_technology_id. This lets the
                // inbox-load logic resolve tech details by structured field
                // instead of parsing the human-readable subject.
                utech_technology_id: techIdForSend,
                ...(ccEmail && { cc_email: ccEmail }),
              };

              try {
                const response = await apiService.sendTrackedEmail(emailPayload);
                if (response.success) {
                  // Only show the "sent" confirmation when the backend ACTUALLY sent it,
                  // or durably queued it for ops to send by hand. (This modal used to be
                  // shown before/regardless of the result, so users saw "sent" even when
                  // the send silently failed — BUG_2026-05-19_orphan_email_conversations.md.)
                  if ((response.data as any)?.queued_for_manual_send) {
                    console.warn('Email could not be sent directly; queued for manual send by ops:',
                      response.data);
                  } else {
                    console.log('Email sent successfully from preview modal:', response.data);
                  }
                  localStorage.setItem('refreshInbox', 'true');
                  setActiveModal(ModalType.SentMessage);
                } else {
                  // The backend could neither send NOR record the message — it really is
                  // lost, so say so rather than showing a phantom success.
                  console.error('Failed to send email from preview modal:', response.error);
                  setActiveModal(ModalType.PreviewMessage);
                  alert(
                    `Your message couldn't be sent: ${response.error || 'an unknown error occurred'}.\n\n` +
                    `Please try again. If it keeps failing, contact info@universitytech.io and we'll route it for you.`
                  );
                }
              } catch (error) {
                console.error('Error sending email from preview modal:', error);
                setActiveModal(ModalType.PreviewMessage);
                alert(
                  "Your message couldn't be sent due to a network error. Please check your connection and try again."
                );
              }
            }}
            techId={selectedCardData?.utech_technology_id || selectedCardData?.id || techCardData.id}
            referenceNumber={selectedCardData?.reference_number || ''}
            techManagerName={contactTechManagerName || selectedCardData?.researchers?.[0]?.name || 'Researcher'}
            techTitle={selectedCardData?.title || techCardData.title}
            message={contactMessage}
            summary={contactSummaryType === 'personal' ? profileData.summary : rdProfileData.rdSummary}
            userName={`${profileData.firstName} ${profileData.lastName}`}
        />
        <SentMessageModal
            isOpen={activeModal === ModalType.SentMessage}
            onClose={() => setActiveModal(null)}
            onGoToInbox={() => setActiveModal(null)}
        />
        <ChangePasswordModal 
            isOpen={activeModal === ModalType.ChangePassword}
            onClose={closeModal}
            onSuccess={() => {
                trackClick('profile_change_password_success');
                // has_password may have flipped from false to true.
                refreshProfileData?.();
            }}
        />
        <DeleteAccountModal 
            isOpen={activeModal === ModalType.DeleteAccount}
            onClose={closeModal}
            onConfirm={handleDeleteAccount}
        />

        {/* Onboarding v2: two skippable steps, then Cosmos. No plans sheet on
            entry — unless the admin turned the entry modal back on. */}
        {showOnboarding && onboardingFlow === 'v2' && onboardingModalConfig && (
        <OnboardingModal
          config={onboardingModalConfig}
          entry={onboardingEntry}
          plan={currentPlanCode}
          onFinish={r => {
            setShowOnboarding(false);
            releasePasswordToast();
            // Signed up from a tech page: back to it now that onboarding is
            // done (finished or skipped — its tracking has already gone).
            if (returnAfterOnboarding()) return;
            // Cosmos lands on this answer: an Explorer's 3 picks for it.
            setExplorerChoice({ ...r, at: Date.now() });
            setActiveView('directsearch');
            if (onboardingConfig?.entry_modal && currentPlanCode === 'explorer') setIntroPricing(true);
          }}
        />
        )}

        {/* Onboarding v1 (flow 'v1', the default until Go live): the legacy
            modal with its callbacks as they were. */}
        {showOnboarding && onboardingFlow === 'v1' && onboardingIndustries.length > 0 && (
        <OnboardingModalLegacy
          industries={onboardingIndustries}
          onSkip={() => {
            setShowOnboarding(false);
            releasePasswordToast();
            if (returnAfterOnboarding()) return;
            // Skipping onboarding is still finishing it, for this purpose.
            if (currentPlanCode === 'explorer') setIntroPricing(true);
          }}
          onExplore={(_industry, _keyword, intent) => {
            setShowOnboarding(false);
            releasePasswordToast();
            // A trial asked for in onboarding stays in the app (the trial
            // sheet opens here); otherwise back to the tech page.
            if (intent !== 'trial' && returnAfterOnboarding()) return;
            if (intent === 'trial') abandonAuthReturn();
            if (intent === 'trial') { setActiveView('directsearch'); void startTrialInPlace(undefined, 'onboarding_legacy'); return; }
            // Show the plans once, over the first screen after onboarding.
            // The modal is mounted here at App level, so it paints over the
            // results they came to see rather than over an empty screen. Not
            // for anyone already paying: a pricing sheet in front of a
            // customer makes them doubt what they bought.
            if (currentPlanCode === 'explorer') setIntroPricing(true);
            setActiveView('directsearch');
          }}
        />
      )}

      {/* The one PricingModal in the app — useUpgradeGate's store depends on
          that. DirectSearchPage used to mount a second one that painted
          underneath this and wired "Upgrade" to the invite form. Stepped
          aside while the trial sheet is up rather than stacked under it:
          both listen for Escape on the document, so one key would have
          closed both, and the sheet is the next step of this modal, not a
          window over it. It comes back if the sheet is dismissed. */}
      {(upgrade.lockedFeature || introPricing) && upgradePlans.length > 0 && !trialForm && (
        <PricingModal
          plans={upgradePlans}
          featureOrder={upgradeFeatureOrder}
          // Was hardcoded to 'explorer', so the paywall told a Navigator
          // subscriber their current plan was Explorer. A TRIAL of a plan is
          // not that plan being current: they have not bought it, and marking
          // it current hid the only button that lets them.
          currentPlanCode={onTrial ? 'explorer' : currentPlanCode}
          lockedFeature={upgrade.lockedFeature}
          busyPlan={upgradeBusyPlan}
          trial={upgradeTrial}
          state={upgradeState}
          trialDaysLeft={upgradeTrialLifecycle?.days_left ?? null}
          onStartTrial={plan => { void startTrialInPlace(plan, `paywall:${upgrade.lockedFeature || 'plans'}`); }}
          // The sign-in prompt's own headline, only while it is what opened
          // the sheet (a lock raised later gets the normal title).
          intro={signinIntro && upgrade.lockedFeature === promptFeature('signin_modal')
            ? { headline: fillUpgradeText(signinIntro.headline, prompts.viewer),
                message: fillUpgradeText(signinIntro.message, prompts.viewer) }
            : undefined}
          onChoose={async (plan, coupon, payMethod, _expected, payOnce) => {
            // Opens Razorpay in place. It used to close the modal and navigate
            // to the Billing tab, making the user find and click the plan a
            // second time at the exact moment they had decided to pay.
            setUpgradeBusyPlan(plan.code);
            await startPlanCheckout({
              userId: apiService.getStoredUserId() || billingUidRef.current,
              plan,
              email: loggedInEmail || '',
              name: `${profileData.firstName} ${profileData.lastName}`.trim(),
              // Applied on the modal itself, so it has to travel with the plan.
              coupon,
              payMethod,
              // A code in USD / EUR / CHF is redeemed on a one-year payment.
              ...(payOnce ? { flow: 'order' as const } : {}),
              onSuccess: () => {
                setIntroPricing(false);
                setSigninIntro(null);
                upgrade.closeUpgrade();
                // Lift the locks now rather than on the next reload.
                upgrade.refresh();
                refreshProfileData?.();
                void reloadBillingState();
              },
              onError: (m) => alert(m),
              onSettled: () => setUpgradeBusyPlan(''),
            });
          }}
          onClose={() => {
            if (upgrade.lockedFeature === promptFeature('signin_modal')) {
              trackUpgradePrompt('dismiss', 'signin_modal', prompts.viewer.audience);
            }
            setIntroPricing(false); setSigninIntro(null); upgrade.closeUpgrade();
          }}
        />
      )}

      {/* The trial details sheet, above the paywall it was opened from and
          above onboarding (z-[60], like onboarding — whichever opened it has
          already stepped aside). */}
      {trialForm && (
        <TrialFormModal
          open
          initial={trialForm.initial}
          days={trialForm.trialInfo?.days || 3}
          onSubmit={submitTrialInPlace}
          onClose={() => { trackClick('trial_form_close', { origin: trialForm.origin }); setTrialForm(null); }}
        />
      )}

      {/* "Your trial is active" — the same toast shape as the password one
          below, in green. */}
      {trialNotice && (
        <div className="fixed inset-x-0 top-0 z-[100] flex justify-center pt-4 sm:pt-6 pointer-events-none">
          <div role="status" className="pointer-events-auto max-w-sm bg-green-50 border border-green-300 rounded-xl shadow-lg px-5 py-4 flex items-start gap-3 animate-fade-in font-space-grotesk">
            <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="flex-1 text-sm font-medium text-green-800">{trialNotice}</p>
            <button onClick={() => setTrialNotice('')} aria-label="Dismiss"
              className="text-green-500 hover:text-green-700 mt-0.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}

      <ProfileCompletionPopup
            isVisible={showProfileCompletionPopup}
            completionPercentage={profileCompletionPercentage}
            onClose={() => {
                trackClick('complete_profile_dismiss', { percentage: profileCompletionPercentage });
                setShowProfileCompletionPopup(false);
            }}
            onCompleteProfile={() => {
                // Was reporting itself as 'nav_click_profile_ring', which both
                // inflated the ring's numbers and left this CTA with none.
                trackClick('complete_profile_click_cta', {
                    percentage: profileCompletionPercentage, source: 'cosmos_popup',
                });
                setShowProfileCompletionPopup(false);
                setActiveView('profile');
            }}
        />

        {/* Password setup toast */}
        {showPasswordToast && (
          <div className="fixed inset-x-0 top-0 z-[100] flex justify-center pt-4 sm:pt-6 pointer-events-none">
            <div className="pointer-events-auto max-w-sm bg-amber-50 border border-amber-300 rounded-xl shadow-lg px-5 py-4 flex items-start gap-3 animate-fade-in font-space-grotesk">
              <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
              </svg>
              <div className="flex-1 text-sm">
                <p className="font-medium text-amber-800">Password not set</p>
                <p className="text-amber-700 mt-0.5">
                  It looks like you haven't set up a password yet. Please set one in{' '}
                  <button className="underline font-semibold cursor-pointer" onClick={() => { setShowPasswordToast(false); setActiveView('profile'); setProfileInitialTab('profile'); }}>
                    Settings
                  </button>{' '}
                  or use the <span className="font-semibold">Forgot Password</span> option.
                </p>
              </div>
              <button onClick={() => setShowPasswordToast(false)} className="text-amber-400 hover:text-amber-600 mt-0.5">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>
        )}
    </div>
  );
};

export default App;